import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  Product, 
  Category, 
  PageSEO, 
  SitePagesContent, 
  OrderInquiry, 
  ProductCategory,
  OrderStatus,
  StoreBackupData,
  NewsTickerSettings,
  NewsTickerItem,
  SavedCustomTag,
  TagDisplaySize
} from '../types';
import { 
  trackPageView, 
  trackRealClick, 
  trackSearchQuery 
} from '../utils/realtimeAnalytics';
import { normalizeImageToWebp } from '../utils/imageToWebp';
import { verifyEncryptedAdminCredentials } from '../utils/adminVault';
import { isProductAnOffer } from '../utils/offerUtils';
import { executeSmartMerge } from '../utils/smartMerge';
import { 
  createAndDownloadStoreZipBackup, 
  parseAndRestoreStoreBackup, 
  createAndDownloadStoriesZipBackup, 
  exportStoriesJsonBackup, 
  parseAndRestoreStoriesBackup, 
  BackupProgressCallback 
} from '../utils/zipBackup';
import { normalizeCategoryFolder } from '../utils/categoryFolders';
import { 
  checkHostingHealth, 
  syncCatalogToHosting, 
  HostingHealthReport, 
  SyncProgressCallback,
  fetchHostingBlogArticles,
  saveHostingBlogArticle,
  deleteHostingBlogArticle,
  saveAllHostingBlogArticles,
  resetHostingBlogAnalytics
} from '../utils/hostingSync';
import { 
  autoSaveProductToHosting, 
  autoSyncCatalogToHosting, 
  saveStoriesToHosting, 
  fetchStoriesFromHosting 
} from '../utils/hostingApi';
import { 
  saveProductsToDb, 
  loadProductsFromDb, 
  saveCategoriesToDb, 
  loadCategoriesFromDb, 
  saveSavedTagsToDb, 
  loadSavedTagsFromDb,
  saveStoriesToDb,
  loadStoriesFromDb
} from '../utils/persistentDb';
import { syncSitemapToServer, SitemapSyncResult } from '../utils/sitemapSync';
import { syncRssToServer, RssSyncResult } from '../utils/rssSync';
import { Language, translations, Translations } from '../data/translations';
import {
  enrichProductWithArabic,
  matchProductByIdentifier,
  resolveCategoryIdFromSlug,
  getProductPermalink,
  getCategoryPermalink,
  getProductLocalizedTitle,
  getCategoryLocalizedName
} from '../utils/localization';
import { 
  INITIAL_PRODUCTS, 
  INITIAL_CATEGORIES, 
  INITIAL_PAGES_CONTENT, 
  INITIAL_PAGES_SEO,
  INITIAL_SAMPLE_ORDERS,
  DEFAULT_NEWS_TICKER,
  DEFAULT_STORIES 
} from '../data/initialData';
import { BlogArticle } from '../types/blog';
import { INITIAL_BLOG_ARTICLES } from '../data/blogArticles';

interface WhatsAppOrderPayload {
  product: Product;
  selectedVariants: Record<string, string>;
  customerName: string;
  customerPhone: string;
  customerCity: string;
  customNote?: string;
}

interface StoreContextType {
  products: Product[];
  isInitialLoading: boolean;
  categories: Category[];
  pagesContent: SitePagesContent;
  pagesSEO: PageSEO[];
  orders: OrderInquiry[];
  
  // Navigation & View
  currentView: string;
  setCurrentView: (view: string) => void;
  activeCategoryFilter: string;
  setActiveCategoryFilter: (cat: string) => void;
  activeTagFilter: string | null;
  setActiveTagFilter: (tag: string | null) => void;
  filterByTagAndCategory: (tag: string, category?: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  
  // Product Detail Modal
  selectedProduct: Product | null;
  setSelectedProduct: (p: Product | null) => void;
  
  // Currency & Language
  currency: 'USD' | 'LBP';
  setCurrency: (c: 'USD' | 'LBP') => void;
  formatPrice: (usd: number) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  isRtl: boolean;
  
  // Order & WhatsApp
  createAndSendWhatsAppOrder: (payload: WhatsAppOrderPayload) => { orderId: string; whatsappUrl: string };
  getOrderByIdOrPhone: (query: string) => OrderInquiry[];
  userInquiryHistory: OrderInquiry[];
  
  // Admin Operations
  isAdminAuthenticated: boolean;
  setIsAdminAuthenticated: (auth: boolean) => void;
  verifyAdminCredentials: (username: string, pass: string) => boolean;
  completeAdminLogin: () => void;
  authenticateAdmin: (pin: string) => boolean;
  logoutAdmin: () => void;
  
  addProduct: (product: Omit<Product, 'id'>) => Product;
  updateProduct: (id: string, updates: Partial<Product>) => void;
  deleteProduct: (id: string) => void;
  savedCustomTags: SavedCustomTag[];
  addSavedCustomTag: (tag: { text: string; color: string }) => void;
  deleteSavedCustomTag: (tagText: string) => void;
  updateSavedCustomTag: (oldText: string, newTag: { text: string; color: string }) => void;
  setProductCustomTag: (productId: string, tag?: { text: string; color: string }) => void;
  setProductsCustomTagBulk: (productIds: string[], tag?: { text: string; color: string }) => void;
  updateGlobalCustomTag: (oldTagText: string, newTag: { text: string; color: string }) => void;
  removeGlobalCustomTag: (tagText: string) => void;
  removeAllCustomTags: () => void;
  moveToRecycleBin: (ids: string | string[]) => void;
  restoreFromRecycleBin: (ids: string | string[]) => void;
  deletePermanently: (ids: string | string[]) => void;
  emptyRecycleBin: () => void;
  setProductsListedStatus: (ids: string | string[], unlisted: boolean) => void;
  resetDefaultCatalog: () => void;
  clearAllProducts: () => void;
  exportStoreBackup: () => void;
  exportFullStoreZipBackup: (onProgress?: BackupProgressCallback) => Promise<void>;
  restoreStoreBackup: (jsonData: string, mode?: 'merge' | 'replace', options?: { untrashAll?: boolean }) => { success: boolean; message: string; count: number };
  restoreStoreBackupFile: (file: File, mode?: 'merge' | 'replace', onProgress?: BackupProgressCallback, options?: { untrashAll?: boolean }) => Promise<{ success: boolean; message: string; count: number; imagesRestored: number }>;
  exportStoriesBackup: () => void;
  exportStoriesZipBackup: (onProgress?: BackupProgressCallback) => Promise<void>;
  restoreStoriesBackupFile: (file: File, mode?: 'merge' | 'replace', onProgress?: BackupProgressCallback) => Promise<{ success: boolean; message: string; count: number; imagesRestored: number }>;
  
  addCategory: (cat: Category) => void;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  deleteCategory: (id: string) => void;
  updateCategoriesBulk: (ids: string[], updates: Partial<Category>) => void;
  deleteCategoriesBulk: (ids: string[]) => void;
  
  updatePagesContent: (content: Partial<SitePagesContent>) => void;
  updatePageSEO: (pageId: string, seo: Partial<PageSEO>) => void;
  saveAllPagesSEO: (pages: PageSEO[]) => Promise<boolean>;
  addPageSEO: (page: PageSEO) => Promise<boolean>;
  deletePageSEO: (pageId: string) => Promise<boolean>;
  resetPagesSEO: () => Promise<boolean>;
  
  // News Ticker
  newsTicker: NewsTickerSettings;
  updateNewsTicker: (settings: Partial<NewsTickerSettings>) => void;
  addNewsTickerItem: (item: Omit<NewsTickerItem, 'id'>) => void;
  updateNewsTickerItem: (id: string, updates: Partial<NewsTickerItem>) => void;
  deleteNewsTickerItem: (id: string) => void;
  resetNewsTickerDefaults: () => void;
  
  updateOrderStatus: (orderId: string, newStatus: OrderStatus, note?: string) => void;
  moveOrdersToRecycleBin: (ids: string | string[]) => void;
  restoreOrdersFromRecycleBin: (ids: string | string[]) => void;
  deleteOrdersPermanently: (ids: string | string[]) => void;
  emptyOrdersRecycleBin: () => void;
  
  // Offers Page Management
  isProductAnOffer: (p: Product) => boolean;
  toggleProductOffersPage: (productId: string, show?: boolean) => void;
  setProductsOffersBulk: (productIds: string[], show: boolean) => void;
  assignNewOfferTagToProduct: (productId: string) => void;
  
  // Product Tag Size Setting
  tagDisplaySize: TagDisplaySize;
  setTagDisplaySize: (size: TagDisplaySize) => void;
  
  // Hosting Database & Server Status
  hostingHealth: HostingHealthReport | null;
  refreshHostingHealth: () => Promise<HostingHealthReport>;
  syncToHostingDb: (onProgress?: SyncProgressCallback) => Promise<{
    success: boolean;
    message: string;
    imagesUploaded: number;
    serverCount: number;
    storage: string;
  }>;

  // Dynamic sitemap.xml & rss.xml
  regenerateAndSyncSitemap: (customBaseUrl?: string) => Promise<SitemapSyncResult>;
  regenerateAndSyncRss: (customBaseUrl?: string) => Promise<RssSyncResult>;

  // Blog Articles & City Guides
  blogArticles: BlogArticle[];
  selectedBlogSlug: string | null;
  setSelectedBlogSlug: (slug: string | null) => void;
  addBlogArticle: (article: Omit<BlogArticle, 'id' | 'publishedAt' | 'updatedAt'>) => void;
  updateBlogArticle: (id: string, updates: Partial<BlogArticle>) => void;
  deleteBlogArticle: (id: string) => void;
  toggleArchiveBlogArticle: (id: string) => void;
  resetBlogArticles: () => void;
  recordBlogArticleView: (slugOrId: string, device?: 'mobile' | 'desktop' | 'tablet', referrer?: 'google' | 'whatsapp' | 'social' | 'direct') => void;
  recordBlogWhatsappClick: (slugOrId: string) => void;
  recordBlogReadDuration: (slugOrId: string, seconds: number) => void;
  resetBlogAnalytics: () => Promise<void>;
  applyStoreStateSnapshot: (snapshot: {
    products?: Product[];
    categories?: Category[];
    pagesContent?: SitePagesContent;
    blogArticles?: BlogArticle[];
    pagesSEO?: PageSEO[];
  }) => void;

  // Quick notice toast
  toastMessage: string | null;
  showToast: (msg: string) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const WHATSAPP_PHONE_RAW = '96181773349'; // +961 81773349
const LBP_RATE = 89500;

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Set to 0 on mount so initial server load is NEVER blocked by mutation cooldown
  const lastLocalMutationTimestamp = useRef<number>(0);
  const isRestoringRef = useRef<boolean>(false);
  const hasLoadedInitialRef = useRef<boolean>(false);

  // Initialize products immediately from localStorage cache or bundled INITIAL_PRODUCTS
  const [products, setProducts] = useState<Product[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('coveristic_products_v3');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.map(enrichProductWithArabic);
          }
        }
      } catch (e) {
        console.warn('Failed to parse cached products from localStorage', e);
      }
    }
    return INITIAL_PRODUCTS.map(enrichProductWithArabic);
  });

  // Track initial product fetch status: true if no cached products are ready, false if already populated
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('coveristic_products_v3');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return false;
          }
        }
      } catch {}
    }
    return true;
  });

  const [categories, setCategories] = useState<Category[]>(() => {
    try {
      const saved = localStorage.getItem('coveristic_categories_v3');
      if (saved) {
        const parsed: Category[] = JSON.parse(saved);
        const mapped = parsed.map(c => {
          let updated = { ...c };
          if (updated.id === 'photo-frames' && updated.tagline?.includes('A4 & A3')) {
            updated.tagline = 'Custom Framed Wall & Desk Photos';
            updated.description = 'Turn your family portraits, wedding memories, graduation moments, and artwork into timeless framed prints with protective crystal acrylic glass.';
          }
          if (updated.heroImage && updated.heroImage.includes('images.unsplash.com')) {
            const initMatch = INITIAL_CATEGORIES.find(x => x.id === updated.id);
            if (initMatch?.heroImage) {
              updated.heroImage = initMatch.heroImage;
            }
          }
          return updated;
        });
        // Auto-merge newly added categories (like wood-crafts, keychains, photo-frames)
        INITIAL_CATEGORIES.forEach(initCat => {
          if (!mapped.some(c => c.id === initCat.id)) {
            mapped.push(initCat);
          }
        });
        return mapped;
      }
      return INITIAL_CATEGORIES;
    } catch {
      return INITIAL_CATEGORIES;
    }
  });

  const [pagesContent, setPagesContent] = useState<SitePagesContent>(() => {
    try {
      const tickerResetDone = localStorage.getItem('coveristic_ticker_default_off_v1');
      const saved = localStorage.getItem('coveristic_pages_content_v6');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!tickerResetDone) {
          localStorage.setItem('coveristic_ticker_default_off_v1', 'true');
          if (parsed.newsTicker) {
            parsed.newsTicker.enabled = false;
          }
        }
        return {
          ...INITIAL_PAGES_CONTENT,
          ...parsed,
          storiesSettings: parsed.storiesSettings
            ? {
                ...DEFAULT_STORIES,
                ...parsed.storiesSettings,
                label: parsed.storiesSettings.label && parsed.storiesSettings.label !== 'FEATURED DROPS & STORIES'
                  ? parsed.storiesSettings.label
                  : 'COVERISTIC STORIES & DROPS',
                items: Array.isArray(parsed.storiesSettings.items) && parsed.storiesSettings.items.length > 0
                  ? parsed.storiesSettings.items.map((it: any) => ({
                      ...it,
                      images: Array.isArray(it.images) && it.images.length > 0
                        ? it.images
                        : it.image ? [it.image] : []
                    }))
                  : DEFAULT_STORIES.items,
              }
            : DEFAULT_STORIES,
          newsTicker: parsed.newsTicker
            ? {
                ...DEFAULT_NEWS_TICKER,
                ...parsed.newsTicker,
                enabled: !tickerResetDone ? false : Boolean(parsed.newsTicker.enabled),
                items: Array.isArray(parsed.newsTicker.items) && parsed.newsTicker.items.length > 0
                  ? parsed.newsTicker.items
                  : DEFAULT_NEWS_TICKER.items,
              }
            : DEFAULT_NEWS_TICKER,
        };
      }
      return INITIAL_PAGES_CONTENT;
    } catch {
      return INITIAL_PAGES_CONTENT;
    }
  });

  const [pagesSEO, setPagesSEO] = useState<PageSEO[]>(() => {
    try {
      const saved = localStorage.getItem('coveristic_pages_seo_v2');
      if (saved) {
        const parsed: PageSEO[] = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const sanitized = parsed.map((p) => ({
            ...p,
            canonicalUrl: p.canonicalUrl
              ? p.canonicalUrl
                  .replace(/https?:\/\/ais-dev[^\/]+/gi, 'https://coveristic.net')
                  .replace(/https?:\/\/[a-z0-9-]+\.europe-west2\.run\.app/gi, 'https://coveristic.net')
                  .replace(/https?:\/\/coveristic\.(lb|com|org)/gi, 'https://coveristic.net')
              : `https://coveristic.net${p.path === '/' ? '' : p.path}`
          }));

          // Merge any newly introduced default pages (such as photo-frames)
          INITIAL_PAGES_SEO.forEach((defaultPage) => {
            if (!sanitized.some((p) => p.id === defaultPage.id || p.path === defaultPage.path)) {
              sanitized.push(defaultPage);
            }
          });

          return sanitized;
        }
      }
      return INITIAL_PAGES_SEO;
    } catch {
      return INITIAL_PAGES_SEO;
    }
  });

  const [orders, setOrders] = useState<OrderInquiry[]>(() => {
    try {
      const saved = localStorage.getItem('coveristic_orders_v2');
      return saved ? JSON.parse(saved) : INITIAL_SAMPLE_ORDERS;
    } catch {
      return INITIAL_SAMPLE_ORDERS;
    }
  });

  const [savedCustomTags, setSavedCustomTags] = useState<SavedCustomTag[]>(() => {
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem('coveristic_saved_tags_v1', JSON.stringify(savedCustomTags));
    } catch (e) {
      console.warn('Failed to persist saved tags', e);
    }
  }, [savedCustomTags]);

  // 30 Lebanese City Print on Demand Blog Articles
  const [blogArticles, setBlogArticles] = useState<BlogArticle[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('coveristic_blog_articles_v2');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        }
      } catch (e) {
        console.warn('Failed to parse blog articles from localStorage', e);
      }
    }
    return INITIAL_BLOG_ARTICLES;
  });

  const [selectedBlogSlug, setSelectedBlogSlug] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
      const hash = window.location.hash.toLowerCase();
      if (path.startsWith('/blog/')) {
        return decodeURIComponent(path.replace(/^\/blog\//, ''));
      }
      if (hash.startsWith('#blog/')) {
        return decodeURIComponent(hash.replace(/^#blog\//, ''));
      }
    }
    return null;
  });

  useEffect(() => {
    try {
      localStorage.setItem('coveristic_blog_articles_v2', JSON.stringify(blogArticles));
    } catch (e) {
      console.warn('Failed to persist blog articles', e);
    }
  }, [blogArticles]);

  // Fetch authoritative blog articles from hosting backend (MySQL /api/blog.php) on mount
  useEffect(() => {
    let isMounted = true;
    fetchHostingBlogArticles().then(serverArticles => {
      if (isMounted && serverArticles && Array.isArray(serverArticles) && serverArticles.length > 0) {
        setBlogArticles(serverArticles);
        try {
          localStorage.setItem('coveristic_blog_articles_v2', JSON.stringify(serverArticles));
        } catch {}
      }
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  // Fetch authoritative page SEO metadata from /api/seo (or /seo.json) on mount
  useEffect(() => {
    let isMounted = true;
    const loadSeoFromApi = async () => {
      const endpoints = [`/api/seo?t=${Date.now()}`, `/api/seo.php?t=${Date.now()}`, `/seo.json?t=${Date.now()}`, `/data/seo.json?t=${Date.now()}`];
      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            cache: 'no-store',
            headers: { 'Accept': 'application/json' }
          });
          if (!res.ok) continue;
          const data = await res.json();
          const list: PageSEO[] | null = Array.isArray(data)
            ? data
            : (data && Array.isArray(data.pagesSEO) ? data.pagesSEO : null);
          if (list && list.length > 0 && isMounted) {
            const merged = [...list];
            INITIAL_PAGES_SEO.forEach(defPage => {
              if (!merged.some(p => p.id === defPage.id || p.path === defPage.path)) {
                merged.push(defPage);
              }
            });
            setPagesSEO(merged);
            try {
              localStorage.setItem('coveristic_pages_seo_v2', JSON.stringify(merged));
            } catch {}
            break;
          }
        } catch {
          // Try next fallback endpoint
        }
      }
    };
    loadSeoFromApi();
    return () => { isMounted = false; };
  }, []);

  // Helper to strip /ar prefix and detect Arabic URL routes
  const parseBrowserPathname = (rawPath: string) => {
    let decoded = rawPath || '/';
    try {
      decoded = decodeURIComponent(rawPath || '/');
    } catch {}
    const clean = decoded.replace(/\/+$/, '') || '/';
    const isArPath = /^\/ar(\/|$)/i.test(clean);
    const stripped = isArPath ? (clean.replace(/^\/ar(\/|$)/i, '/') || '/') : clean;
    return {
      isArPath,
      stripped,
      strippedLower: stripped.toLowerCase(),
    };
  };

  // State controls
  const [currentView, setCurrentView] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const { strippedLower: path } = parseBrowserPathname(window.location.pathname);
      const search = new URLSearchParams(window.location.search);
      const hash = window.location.hash.toLowerCase();

      const queryPage = search.get('page') || search.get('view');
      if (queryPage) {
        return queryPage.toLowerCase();
      }

      if (path === '/search-console' || hash === '#search-console' || path === '/analytics' || hash === '#analytics') {
        return 'search-console';
      }
      if (path === '/admin' || hash === '#admin') {
        return 'admin';
      }
      if (path === '/offers' || hash === '#offers') {
        return 'offers';
      }
      if (path === '/custom-print' || hash === '#custom-print') {
        return 'custom-print';
      }
      if (path === '/about' || hash === '#about') {
        return 'about';
      }
      if (path === '/contact' || hash === '#contact') {
        return 'contact';
      }
      if (path === '/terms' || path === '/terms-of-service' || hash === '#terms') {
        return 'terms';
      }
      if (path === '/privacy' || path === '/privacy-policy' || hash === '#privacy') {
        return 'privacy';
      }
      if (path === '/pricing' || hash === '#pricing') {
        return 'pricing';
      }
      if (path === '/sitemap' || hash === '#sitemap') {
        return 'admin';
      }
      if (path === '/rss' || path === '/rss.xml' || path === '/rss-ar' || path === '/rss-ar.xml' || hash === '#rss') {
        return 'rss';
      }
      if (path === '/blog' || hash === '#blog' || path.startsWith('/blog/') || hash.startsWith('#blog/')) {
        return 'blog';
      }
      if (path.startsWith('/category/') || path === '/shop' || hash === '#shop') {
        return 'shop';
      }
      if (path !== '/' && path !== '/index.html' && !path.startsWith('/product/')) {
        return '404';
      }
    }
    return 'home';
  });

  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const { strippedLower: path } = parseBrowserPathname(window.location.pathname);
      const search = new URLSearchParams(window.location.search);
      if (path.startsWith('/category/')) {
        const rawSlug = path.replace(/^\/category\//, '');
        return resolveCategoryIdFromSlug(rawSlug) || rawSlug;
      }
      if (search.has('category')) {
        const rawSlug = search.get('category') || 'all';
        return resolveCategoryIdFromSlug(rawSlug) || rawSlug.toLowerCase();
      }
    }
    return 'all';
  });
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const isInitialUrlResolved = useRef(false);

  const currency: 'USD' | 'LBP' = 'USD';
  const setCurrency = (_c: 'USD' | 'LBP') => {
    // Only USD is accepted as requested
  };

  // Detect whether user's browser or OS language is Arabic
  const isBrowserLanguageArabic = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const langs = Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language || (navigator as any).userLanguage || ''];
    const primary = String(langs[0] || '').toLowerCase();
    return primary.startsWith('ar');
  };

  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const { isArPath, strippedLower } = parseBrowserPathname(window.location.pathname);
      if (isArPath || strippedLower === '/rss-ar' || strippedLower === '/rss-ar.xml') return 'ar';
      const search = new URLSearchParams(window.location.search);
      if (search.get('lang') === 'ar') return 'ar';
      if (search.get('lang') === 'en') return 'en';

      try {
        const manualPref = localStorage.getItem('coveristic_lang_manual');
        if (manualPref === 'ar' || manualPref === 'en') {
          return manualPref;
        }
      } catch {}

      // Instantly show Arabic version if user's browser/system language is Arabic
      if (isBrowserLanguageArabic()) {
        return 'ar';
      }
    }
    return 'en';
  });

  const setLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    try {
      localStorage.setItem('coveristic_lang', newLang);
      localStorage.setItem('coveristic_lang_manual', newLang);
    } catch {
      // ignore
    }
  };

  // Parse and apply current browser URL permalinks (supports both /... and /ar/... with Arabic slugs)
  const resolvePermalinkFromUrl = (currentProducts: Product[], currentCategories: Category[]) => {
    if (typeof window === 'undefined') return;
    const rawPath = window.location.pathname;
    const { isArPath, stripped, strippedLower: normalizedPath } = parseBrowserPathname(rawPath);
    const search = new URLSearchParams(window.location.search);
    const hash = window.location.hash.toLowerCase();

    if (isArPath && language !== 'ar') {
      setLanguageState('ar');
    } else if (!isArPath && search.get('lang') !== 'ar' && language !== 'en') {
      // Only switch back to 'en' if user explicitly navigated to an English URL after initial load,
      // or if manual preference is 'en' (preserve auto-detected Arabic on first load)
      let manualPref: string | null = null;
      try {
        manualPref = localStorage.getItem('coveristic_lang_manual');
      } catch {}
      if (isInitialUrlResolved.current || manualPref === 'en') {
        setLanguageState('en');
      }
    }

    // 1. Product Permalink: /product/:slugOrId or /ar/product/:arabicSlug or ?product=:id
    let productIdentifier: string | null = null;
    if (normalizedPath.startsWith('/product/')) {
      productIdentifier = stripped.replace(/^\/product\//i, '').replace(/\/+$/, '');
    } else if (search.has('product')) {
      productIdentifier = search.get('product');
    }

    if (productIdentifier) {
      const match = matchProductByIdentifier(currentProducts, productIdentifier);
      if (match) {
        setSelectedProduct(match);
        return;
      } else if (currentProducts.length > 0) {
        setSelectedProduct(null);
        setCurrentView('404');
        return;
      }
    } else {
      setSelectedProduct(null);
    }

    // 2. Category Permalink: /category/:slug or /ar/category/:arabicSlug or ?category=:slug
    let categorySlug: string | null = null;
    if (normalizedPath.startsWith('/category/')) {
      categorySlug = stripped.replace(/^\/category\//i, '').replace(/\/+$/, '');
    } else if (search.has('category') && normalizedPath !== '/rss' && normalizedPath !== '/rss.xml' && hash !== '#rss') {
      categorySlug = search.get('category');
    }

    if (categorySlug) {
      const resolvedCatId = resolveCategoryIdFromSlug(categorySlug, currentCategories);
      if (resolvedCatId) {
        setActiveCategoryFilter(resolvedCatId);
        setCurrentView('shop');
        setSelectedProduct(null);
      } else {
        setCurrentView('404');
        setSelectedProduct(null);
      }
      return;
    }

    // 3. Static Pages Permalinks
    const queryPage = search.get('page') || search.get('view');
    const effectivePage = queryPage ? queryPage.toLowerCase() : null;

    if (normalizedPath === '/search-console' || hash === '#search-console' || normalizedPath === '/analytics' || hash === '#analytics' || effectivePage === 'search-console') {
      setCurrentView('search-console');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/admin' || hash === '#admin' || effectivePage === 'admin') {
      setCurrentView('admin');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/offers' || hash === '#offers' || effectivePage === 'offers') {
      setCurrentView('offers');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/custom-print' || hash === '#custom-print' || effectivePage === 'custom-print') {
      setCurrentView('custom-print');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/about' || hash === '#about' || effectivePage === 'about') {
      setCurrentView('about');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/contact' || hash === '#contact' || effectivePage === 'contact') {
      setCurrentView('contact');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/terms' || normalizedPath === '/terms-of-service' || hash === '#terms' || effectivePage === 'terms') {
      setCurrentView('terms');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/privacy' || normalizedPath === '/privacy-policy' || hash === '#privacy' || effectivePage === 'privacy') {
      setCurrentView('privacy');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/pricing' || hash === '#pricing' || effectivePage === 'pricing') {
      setCurrentView('pricing');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/sitemap' || hash === '#sitemap' || effectivePage === 'sitemap') {
      setCurrentView('admin');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/rss' || normalizedPath === '/rss.xml' || normalizedPath === '/rss-ar' || normalizedPath === '/rss-ar.xml' || hash === '#rss' || effectivePage === 'rss') {
      setCurrentView('rss');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/blog' || hash === '#blog' || normalizedPath.startsWith('/blog/') || hash.startsWith('#blog/') || effectivePage === 'blog') {
      setCurrentView('blog');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
      let blogSlug: string | null = null;
      if (normalizedPath.startsWith('/blog/')) {
        blogSlug = stripped.replace(/^\/blog\//i, '').replace(/\/+$/, '');
      } else if (hash.startsWith('#blog/')) {
        blogSlug = decodeURIComponent(hash.replace(/^#blog\//i, '').replace(/\/+$/, ''));
      } else if (search.has('article') || search.has('slug') || search.has('post')) {
        blogSlug = search.get('article') || search.get('slug') || search.get('post');
      }
      setSelectedBlogSlug(blogSlug);
    } else if (normalizedPath === '/shop' || hash === '#shop' || effectivePage === 'shop') {
      setCurrentView('shop');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else if (normalizedPath === '/' || normalizedPath === '' || normalizedPath === '/index.html') {
      setCurrentView('home');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    } else {
      setCurrentView('404');
      setActiveCategoryFilter('all');
      setSelectedProduct(null);
    }
  };

  // Resolve permalinks on catalog load & popstate (browser back/forward)
  useEffect(() => {
    if (products.length > 0) {
      resolvePermalinkFromUrl(products, categories);
      isInitialUrlResolved.current = true;
    }
  }, [products.length, categories.length]);

  useEffect(() => {
    const handlePopState = () => {
      resolvePermalinkFromUrl(products, categories);
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, [products, categories, language]);

  // Synchronize browser URL bar, document title, canonical link, and hreflang with active permalink & language
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const { strippedLower } = parseBrowserPathname(window.location.pathname);

    // Prevent overwriting initial /product/... or /ar/product/... URL while catalog is first loading
    if (!isInitialUrlResolved.current) {
      if (strippedLower.startsWith('/product/') && !selectedProduct && products.length === 0) {
        return;
      }
      isInitialUrlResolved.current = true;
    }

    if (currentView !== 'blog' && selectedBlogSlug) {
      setSelectedBlogSlug(null);
    }

    const prefix = language === 'ar' ? '/ar' : '';
    let targetPath = language === 'ar' ? '/ar/' : '/';

    if (selectedProduct) {
      targetPath = getProductPermalink(selectedProduct, language);
      const prodTitle = getProductLocalizedTitle(selectedProduct, language);
      document.title = language === 'ar'
        ? `${prodTitle} | كوفريستيك لبنان`
        : `${prodTitle} | Coveristic Lebanon`;
    } else {
      let title = language === 'ar'
        ? 'كوفريستيك لبنان | طباعة كفرات موبايل ولوحات كانفاس وهدايا مخصصة'
        : 'Coveristic | Custom Phone Cases & Canvas Prints Lebanon';

      if (currentView === '404') {
        document.title = language === 'ar' ? '404 الصفحة غير موجودة | كوفريستيك لبنان' : '404 Page Not Found | Coveristic Lebanon';
        return;
      } else if (currentView === 'about') {
        targetPath = `${prefix}/about`;
        title = language === 'ar' ? 'من نحن والأسئلة الشائعة | كوفريستيك لبنان' : 'About Coveristic & FAQs | Custom Prints Lebanon';
      } else if (currentView === 'contact') {
        targetPath = `${prefix}/contact`;
        title = language === 'ar' ? 'تواصل معنا — استوديو الطباعة المخصصة | كوفريستيك لبنان' : 'Contact Us – Custom Print Studio Lebanon | Coveristic';
      } else if (currentView === 'terms') {
        targetPath = `${prefix}/terms`;
        title = language === 'ar' ? 'شروط الخدمة وضمان الطباعة | كوفريستيك لبنان' : 'Terms of Service & Print Guarantee | Coveristic Lebanon';
      } else if (currentView === 'privacy') {
        targetPath = `${prefix}/privacy`;
        title = language === 'ar' ? 'سياسة الخصوصية | كوفريستيك لبنان' : 'Privacy Policy | Coveristic Print Studio Lebanon';
      } else if (currentView === 'pricing') {
        targetPath = `${prefix}/pricing`;
        title = language === 'ar' ? 'قائمة الأسعار بالدولار | كوفريستيك لبنان' : 'Custom Print Pricing Guide (USD) | Coveristic Lebanon';
      } else if (currentView === 'custom-print') {
        targetPath = `${prefix}/custom-print`;
        title = language === 'ar' ? 'استوديو الطباعة حسب الطلب — اطبع صورتك | كوفريستيك لبنان' : 'Custom Print on Demand Studio | Coveristic Lebanon';
      } else if (currentView === 'offers') {
        targetPath = `${prefix}/offers`;
        title = language === 'ar' ? 'العروض الخاصة والباقات الموفرة | كوفريستيك لبنان' : 'Special Offers & Drops | Coveristic';
      } else if (currentView === 'blog') {
        targetPath = selectedBlogSlug ? `${prefix}/blog/${selectedBlogSlug}` : `${prefix}/blog`;
        const activeArt = selectedBlogSlug
          ? blogArticles.find(a => a.slug.toLowerCase() === selectedBlogSlug.toLowerCase() || a.id.toLowerCase() === selectedBlogSlug.toLowerCase())
          : null;
        title = activeArt
          ? `${activeArt.title} | Coveristic Lebanon Print Studio`
          : (language === 'ar' ? 'مدونة الطباعة حسب الطلب ودليل المدن اللبنانية | كوفريستيك' : 'Print on Demand Lebanon Blog & City Guides | Coveristic');
      } else if (currentView === 'admin' || currentView === 'sitemap') {
        targetPath = '/admin';
        title = 'Coveristic Admin Portal';
      } else if (currentView === 'search-console' || currentView === 'analytics') {
        targetPath = '/search-console';
        title = 'Search Console & Real-Time Analytics | Coveristic';
      } else if (currentView === 'rss') {
        targetPath = language === 'ar' ? '/ar/rss' : '/rss';
        title = language === 'ar'
          ? 'خلاصة RSS للمنتجات والأقسام باللغة العربية | كوفريستيك لبنان'
          : 'RSS Feed — Products per Category & Blog | Coveristic';
      } else if (activeCategoryFilter && activeCategoryFilter !== 'all') {
        targetPath = getCategoryPermalink(activeCategoryFilter, language);
        const catName = getCategoryLocalizedName(activeCategoryFilter, language);
        title = language === 'ar'
          ? `${catName} | كوفريستيك للطباعة حسب الطلب في لبنان`
          : `${catName} | Coveristic Custom Prints`;
      } else if (currentView === 'shop') {
        targetPath = `${prefix}/shop`;
        title = language === 'ar' ? 'تصفح جميع المنتجات المطبوعة | كوفريستيك لبنان' : 'Shop Custom Prints | Coveristic Lebanon';
      } else {
        targetPath = language === 'ar' ? '/ar/' : '/';
        title = language === 'ar'
          ? 'كوفريستيك لبنان | طباعة كفرات موبايل ولوحات كانفاس وهدايا مخصصة'
          : 'Coveristic | Custom Phone Cases & Canvas Prints Lebanon';
      }

      if (language !== 'ar' && (currentView !== 'blog' || !selectedBlogSlug)) {
        const matchedSeo = pagesSEO.find(
          p => p.path === targetPath ||
               (activeCategoryFilter && activeCategoryFilter !== 'all' && p.id === activeCategoryFilter) ||
               ((!activeCategoryFilter || activeCategoryFilter === 'all') && p.id === currentView)
        );
        document.title = matchedSeo?.title || title;
      } else {
        document.title = title;
      }
    }

    let currentDecodedPath = window.location.pathname;
    try {
      currentDecodedPath = decodeURIComponent(window.location.pathname);
    } catch {}

    if (currentDecodedPath !== targetPath && window.location.pathname !== targetPath) {
      try {
        window.history.pushState({ view: currentView, category: activeCategoryFilter, blogSlug: selectedBlogSlug, lang: language }, '', targetPath);
      } catch {
        // ignore potential iframe restrictions
      }
    }

    // Update canonical & og:url dynamically so canonical always matches the active page URL
    try {
      const canonicalHref = `https://coveristic.net${targetPath === '/' ? '/' : targetPath}`;
      const canEl = document.getElementById('canonical-link') || document.querySelector('link[rel="canonical"]');
      if (canEl) canEl.setAttribute('href', canonicalHref);
      const ogUrlEl = document.querySelector('meta[property="og:url"]');
      if (ogUrlEl) ogUrlEl.setAttribute('content', canonicalHref);
    } catch {}
  }, [selectedProduct, currentView, activeCategoryFilter, selectedBlogSlug, blogArticles, categories, products.length, pagesSEO, language]);

  const isRtl = language === 'ar';
  const t = translations[language];

  // Synchronize document dir and lang attributes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
      document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
      if (isRtl) {
        document.body.classList.add('rtl');
      } else {
        document.body.classList.remove('rtl');
      }
    }
  }, [language, isRtl]);

  const filterByTagAndCategory = (tag: string, category?: string) => {
    if (category && category !== 'all') {
      setActiveCategoryFilter(category);
    }
    setActiveTagFilter(tag);
    if (currentView !== 'shop' && currentView !== 'home') {
      setCurrentView('shop');
    }
    setSelectedProduct(null);
    setTimeout(() => {
      document.getElementById('featured-products-heading')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Auto-purge recycle bin items older than 30 days (WooCommerce retention policy)
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  useEffect(() => {
    setProducts(prev => {
      const now = Date.now();
      const valid = prev.filter(p => {
        if (!p.inRecycleBin || !p.trashedAt) return true;
        const trashedTime = new Date(p.trashedAt).getTime();
        return (now - trashedTime) < THIRTY_DAYS_MS;
      });
      return valid.length !== prev.length ? valid : prev;
    });

    setOrders(prev => {
      const now = Date.now();
      const valid = prev.filter(o => {
        if (!o.inRecycleBin || !o.trashedAt) return true;
        const trashedTime = new Date(o.trashedAt).getTime();
        return (now - trashedTime) < THIRTY_DAYS_MS;
      });
      return valid.length !== prev.length ? valid : prev;
    });
  }, []);

  // 1. Initial hydration from IndexedDB on mount (covers large datasets and base64 images that exceed localStorage 5MB limit)
  useEffect(() => {
    let isMounted = true;
    loadProductsFromDb()
      .then(dbProducts => {
        if (!isMounted) return;
        if (dbProducts && Array.isArray(dbProducts) && dbProducts.length > 0) {
          const enrichedDb = dbProducts.map(enrichProductWithArabic);
          setProducts(prev => {
            if (prev.length === 0) return enrichedDb;
            const merged = executeSmartMerge(enrichedDb, prev);
            return merged.mergedProducts.map(enrichProductWithArabic);
          });
        }
      })
      .catch(err => console.warn('IndexedDB initial hydration error:', err));

    // Hydrate Coveristic Stories from IndexedDB
    loadStoriesFromDb()
      .then(dbStories => {
        if (!isMounted) return;
        if (dbStories && Array.isArray(dbStories.items) && dbStories.items.length > 0) {
          setPagesContent(prev => ({
            ...prev,
            storiesSettings: {
              ...DEFAULT_STORIES,
              ...dbStories,
              items: dbStories.items.map((it: any) => ({
                ...it,
                images: Array.isArray(it.images) && it.images.length > 0
                  ? it.images
                  : it.image ? [it.image] : []
              }))
            }
          }));
        }
      })
      .catch(err => console.warn('IndexedDB stories hydration error:', err));

    // Hydrate Coveristic Stories from Hosting Server (/data/stories.json or MySQL)
    fetchStoriesFromHosting()
      .then(serverStories => {
        if (!isMounted) return;
        if (serverStories && Array.isArray(serverStories.items) && serverStories.items.length > 0) {
          setPagesContent(prev => ({
            ...prev,
            storiesSettings: {
              ...DEFAULT_STORIES,
              ...serverStories,
              items: serverStories.items.map((it: any) => ({
                ...it,
                images: Array.isArray(it.images) && it.images.length > 0
                  ? it.images
                  : it.image ? [it.image] : []
              }))
            }
          }));
          saveStoriesToDb(serverStories).catch(() => {});
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  // Hosting Server & Database Health state
  const [hostingHealth, setHostingHealth] = useState<HostingHealthReport | null>(null);

  const refreshHostingHealth = async (): Promise<HostingHealthReport> => {
    const report = await checkHostingHealth();
    setHostingHealth(report);
    return report;
  };

  useEffect(() => {
    refreshHostingHealth();
  }, []);

  // Fast & resilient sync from hosting server (dual parallel check: /data/products.json & /api/products.php)
  useEffect(() => {
    const fetchWithTimeout = async (url: string, timeoutMs: number = 3000): Promise<any | null> => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(url, {
          cache: 'no-store',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });
        clearTimeout(timer);
        if (res.ok) {
          return await res.json();
        }
        return null;
      } catch {
        clearTimeout(timer);
        return null;
      }
    };

    let retryTimer: any = null;
    let retryCount = 0;
    const maxRetries = 3;

    const loadAuthoritativeProducts = async () => {
      // Guard: Never overwrite if currently restoring
      if (isRestoringRef.current) {
        return;
      }

      // Mutation cooldown only protects against background overwrites if an authenticated admin is actively editing
      // Storefront visitors and initial page opens are NEVER blocked!
      if (isAdminAuthenticated && lastLocalMutationTimestamp.current > 0 && (Date.now() - lastLocalMutationTimestamp.current < 30000)) {
        hasLoadedInitialRef.current = true;
        setIsInitialLoading(false);
        return;
      }

      let applied = false;

      const applyProductsIfValid = (incoming: any) => {
        const list = Array.isArray(incoming) ? incoming : (incoming?.products && Array.isArray(incoming.products) ? incoming.products : null);
        if (list && list.length > 0) {
          const enrichedList = list.map(enrichProductWithArabic);
          setProducts(prev => {
            if (prev.length === 0 || !isAdminAuthenticated) {
              return enrichedList;
            }
            const merged = executeSmartMerge(prev, enrichedList);
            return merged.mergedProducts.map(enrichProductWithArabic);
          });
          setIsInitialLoading(false);
          hasLoadedInitialRef.current = true;
          applied = true;
          return true;
        }
        return false;
      };

      // 1. Concurrently launch both ultra-fast static file check and dynamic PHP check
      // Static /data/products.json serves directly from disk in <20ms (zero PHP boot or MySQL overhead)
      const staticPromise = fetchWithTimeout(`/data/products.json?t=${Date.now()}`, 2500)
        .then(data => {
          if (!applied) applyProductsIfValid(data);
          return data;
        })
        .catch(() => null);

      // Dynamic /api/products.php queries hosting MySQL or server-side flat file with auto-heal
      const phpPromise = fetchWithTimeout(`/api/products.php?t=${Date.now()}`, 4000)
        .then(data => {
          if (data && data.success && data.products) {
            applyProductsIfValid(data.products);
          }
          return data;
        })
        .catch(() => null);

      await Promise.allSettled([staticPromise, phpPromise]);

      hasLoadedInitialRef.current = true;
      setIsInitialLoading(false);

      // If catalog is still empty (e.g. mobile 3G/4G packet drop or server just rebooted), schedule quick retry
      if (!applied && retryCount < maxRetries) {
        retryCount++;
        retryTimer = setTimeout(() => {
          loadAuthoritativeProducts();
        }, 2000 * retryCount);
      }
    };

    // Execute immediately on mount!
    loadAuthoritativeProducts();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadAuthoritativeProducts();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', loadAuthoritativeProducts);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', loadAuthoritativeProducts);
    };
  }, [isAdminAuthenticated]);

  // 3. Sync products to IndexedDB, localStorage, and hosting server-side products.php
  useEffect(() => {
    // Save to IndexedDB (virtually unlimited capacity, zero QuotaExceededError)
    saveProductsToDb(products).catch(() => {});

    // Save to localStorage safely
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify(products));
    } catch (e) {
      console.warn('localStorage quota exceeded, catalog safely preserved in IndexedDB', e);
    }

    // Only record local mutation timestamp if an authenticated admin is making local catalog changes
    if (isAdminAuthenticated && hasLoadedInitialRef.current) {
      lastLocalMutationTimestamp.current = Date.now();
    }

    // Push to server ONLY if authenticated admin made changes and products exist
    // Never allow storefront visitors, crawlers, or empty client states to POST to /api/products.php
    if (isAdminAuthenticated && hasLoadedInitialRef.current && products.length > 0 && !isRestoringRef.current) {
      autoSyncCatalogToHosting(products).then(() => {
        refreshHostingHealth();
      }).catch(err => {
        console.warn('Automatic catalog sync to hosting database warning:', err);
      });
    }
  }, [products, isAdminAuthenticated]);

  // Hosting Database Sync
  const syncToHostingDb = async (onProgress?: SyncProgressCallback) => {
    isRestoringRef.current = true;
    try {
      const result = await syncCatalogToHosting(products, onProgress);
      if (result.success && result.sanitizedProducts.length > 0) {
        setProducts(result.sanitizedProducts);
        saveProductsToDb(result.sanitizedProducts).catch(() => {});
        try {
          localStorage.setItem('coveristic_products_v3', JSON.stringify(result.sanitizedProducts));
        } catch {}
      }
      refreshHostingHealth();
      return result;
    } finally {
      isRestoringRef.current = false;
      lastLocalMutationTimestamp.current = Date.now();
    }
  };

  useEffect(() => {
    localStorage.setItem('coveristic_categories_v3', JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    try {
      localStorage.setItem('coveristic_pages_content_v6', JSON.stringify(pagesContent));
    } catch (e) {
      console.warn('localStorage quota warning for pagesContent, safe in IndexedDB', e);
    }

    if (pagesContent.storiesSettings) {
      // 1. Always persist stories in IndexedDB database (handles multi-images and large lookbooks)
      saveStoriesToDb(pagesContent.storiesSettings).catch(() => {});

      // 2. Automatically sync to hosting server database (MySQL & stories.json) if admin mutated
      if (isAdminAuthenticated && hasLoadedInitialRef.current && !isRestoringRef.current) {
        saveStoriesToHosting(pagesContent.storiesSettings).catch(err => {
          console.warn('Auto-save stories to hosting warning:', err);
        });
      }
    }
  }, [pagesContent, isAdminAuthenticated]);

  useEffect(() => {
    localStorage.setItem('coveristic_pages_seo_v2', JSON.stringify(pagesSEO));
  }, [pagesSEO]);

  useEffect(() => {
    localStorage.setItem('coveristic_orders_v2', JSON.stringify(orders));
  }, [orders]);

  // Always automatically update sitemap.xml whenever products, categories, pagesSEO, or pagesContent change
  const sitemapDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only auto-sync after initial data has loaded to prevent syncing with empty initial arrays
    if (!hasLoadedInitialRef.current || isRestoringRef.current) {
      return;
    }

    if (sitemapDebounceRef.current) {
      clearTimeout(sitemapDebounceRef.current);
    }

    sitemapDebounceRef.current = setTimeout(() => {
      syncSitemapToServer(products, categories, pagesSEO, pagesContent, undefined, blogArticles).catch(() => {});
      syncRssToServer(products, categories, blogArticles).catch(() => {});
    }, 1000);

    return () => {
      if (sitemapDebounceRef.current) {
        clearTimeout(sitemapDebounceRef.current);
      }
    };
  }, [products, categories, pagesSEO, pagesContent, blogArticles]);

  // Real-time Analytics Event Hooks
  useEffect(() => {
    trackPageView(currentView === 'home' ? '/' : `/${currentView}`, currentView);
  }, [currentView]);

  useEffect(() => {
    if (selectedProduct) {
      trackRealClick('product', selectedProduct.title, `/category/${selectedProduct.category}`);
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (searchQuery && searchQuery.trim().length >= 2) {
      trackSearchQuery(searchQuery.trim());
    }
  }, [searchQuery]);

  // Sync Active SEO to document head (ensuring exact per-route canonical URLs and robots tags for Googlebot)
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const setMetaContent = (selector: string, attrName: 'name' | 'property', attrVal: string, content: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setCanonicalUrl = (url: string) => {
      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', url);
      setMetaContent('meta[property="og:url"]', 'property', 'og:url', url);
    };

    // Ensure admin, rss, sitemap, and search-console views are marked noindex so Google only indexes real store & blog content
    const isUtilityView = !selectedProduct && (currentView === 'admin' || currentView === 'sitemap' || currentView === 'rss' || currentView === 'search-console' || currentView === 'analytics');
    const robotsDirective = isUtilityView
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    setMetaContent('meta[name="robots"]', 'name', 'robots', robotsDirective);
    setMetaContent('meta[name="googlebot"]', 'name', 'googlebot', robotsDirective);

    // 1. Product Detail Permalink SEO
    if (selectedProduct) {
      const slugOrId = selectedProduct.slug || selectedProduct.id;
      const prodCanonical = `https://coveristic.net/product/${slugOrId}`;
      const prodTitle = `${selectedProduct.title} | Coveristic Lebanon`;
      const prodDesc = selectedProduct.description || `Order custom ${selectedProduct.title} ($${selectedProduct.price.toFixed(2)}) at Coveristic Print Studio Lebanon with fast Cash on Delivery.`;

      document.title = prodTitle;
      setMetaContent('meta[name="description"]', 'name', 'description', prodDesc);
      setMetaContent('meta[property="og:title"]', 'property', 'og:title', prodTitle);
      setMetaContent('meta[property="og:description"]', 'property', 'og:description', prodDesc);
      setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', prodTitle);
      setMetaContent('meta[name="twitter:description"]', 'name', 'twitter:description', prodDesc);
      setCanonicalUrl(prodCanonical);
      return;
    }

    // 2. Blog Article Permalink SEO (managed in detail by BlogView, ensure canonical never resets to /)
    if (currentView === 'blog' && selectedBlogSlug) {
      setCanonicalUrl(`https://coveristic.net/blog/${selectedBlogSlug}`);
      return;
    }

    // 3. Category or Static Page SEO from seo.json
    const currentPath =
      activeCategoryFilter && activeCategoryFilter !== 'all'
        ? `/category/${activeCategoryFilter}`
        : currentView === 'home'
        ? '/'
        : `/${currentView}`;

    const activePage =
      (activeCategoryFilter && activeCategoryFilter !== 'all'
        ? pagesSEO.find(p => p.id === activeCategoryFilter || p.path === `/category/${activeCategoryFilter}`)
        : null) ||
      pagesSEO.find(p => p.id === currentView || p.path === currentPath) ||
      (currentPath === '/' ? pagesSEO[0] : null);

    const exactCanonical =
      activePage?.canonicalUrl && activePage.path === currentPath
        ? activePage.canonicalUrl
        : `https://coveristic.net${currentPath === '/' ? '/' : currentPath}`;

    setCanonicalUrl(exactCanonical);

    if (activePage) {
      document.title = activePage.title;
      setMetaContent('meta[name="description"]', 'name', 'description', activePage.description);
      if (activePage.keywords) {
        setMetaContent('meta[name="keywords"]', 'name', 'keywords', activePage.keywords);
      }
      setMetaContent('meta[property="og:title"]', 'property', 'og:title', activePage.title);
      setMetaContent('meta[property="og:description"]', 'property', 'og:description', activePage.description);
      setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', activePage.title);
      setMetaContent('meta[name="twitter:description"]', 'name', 'twitter:description', activePage.description);
    }
  }, [currentView, activeCategoryFilter, selectedProduct, selectedBlogSlug, pagesSEO]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(prev => (prev === msg ? null : prev));
    }, 3500);
  };

  const formatPrice = (usd: number) => {
    return `$${usd.toFixed(2)}`;
  };

  const verifyAdminCredentials = (username: string, pass: string): boolean => {
    return verifyEncryptedAdminCredentials(username, pass);
  };

  const completeAdminLogin = () => {
    setIsAdminAuthenticated(true);
    showToast('Administrator session verified and unlocked.');
  };

  const authenticateAdmin = (_pinOrPass: string): boolean => {
    // Direct 1-step login is strictly disabled; 2-Step OTP verification is required
    return false;
  };

  const logoutAdmin = () => {
    setIsAdminAuthenticated(false);
    try {
      sessionStorage.removeItem('coveristic_admin_auth');
    } catch {}
    showToast('Admin session terminated safely.');
  };

  // WhatsApp Order Forwarding
  const createAndSendWhatsAppOrder = (payload: WhatsAppOrderPayload) => {
    const orderNum = Math.floor(10000 + Math.random() * 90000);
    const orderId = `COV-${orderNum}`;
    const dateStr = new Date().toISOString();

    const variantDetailsList = Object.entries(payload.selectedVariants)
      .map(([k, v]) => `• ${k}: *${v}*`)
      .join('\n');

    const displayProductTitle = language === 'ar'
      ? getProductLocalizedTitle(payload.product, 'ar')
      : (payload.product.category === 'phone-cases' ? 'Custom Phone case' : payload.product.title);

    const productLink = `${window.location.origin}${getProductPermalink(payload.product, language)}`;

    const messageText = language === 'ar'
      ? `مرحباً Coveristic! 👋 أود طلب هذا المنتج بالطباعة المخصصة:
────────────────────────
🆔 *رقم الطلب:* #${orderId}
📦 *المنتج:* ${displayProductTitle}
💰 *السعر:* ${formatPrice(payload.product.price)}

⚙️ *المواصفات المختارة:*
${variantDetailsList}
${payload.customNote ? `\n📝 *ملاحظات خاصة على الطلب:* \n${payload.customNote}\n` : ''}
👤 *الاسم:* ${payload.customerName || 'عميل'}
📍 *العنوان / المنطقة:* ${payload.customerCity || 'لبنان'}

🖼️ *إرفاق الصورة أو التصميم:*
سأقوم بإرسال الصورة أو التصميم هنا عبر المحادثة لاعتماد البروفة والمعاينة قبل البدء بالطباعة.

🔗 *رابط المنتج:* ${productLink}
────────────────────────
_تم الإرسال عبر استوديو Coveristic لبنان_`
      : `👋 *NEW CUSTOM PRINT ORDER INQUIRY – COVERISTIC*
────────────────────────
🆔 *Order Ref:* #${orderId}
📦 *Product:* ${displayProductTitle}
💰 *Price:* $${payload.product.price.toFixed(2)}

⚙️ *Custom Options Selected:*
${variantDetailsList}
${payload.customNote ? `\n📝 *Custom Notes & Requests:*\n${payload.customNote}\n` : ''}
🖼️ *Custom Image Attachment:*
_I will attach my personal photo/artwork directly in this WhatsApp chat — image preview before printing request is available before printing._

🔗 *Product Ref:* ${window.location.origin}/?product=${payload.product.id}
────────────────────────
_Sent via Coveristic Custom Print on Demand Studio._`;

    const encoded = encodeURIComponent(messageText);
    const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE_RAW}?text=${encoded}`;

    const newOrder: OrderInquiry = {
      id: orderId,
      date: dateStr,
      customerName: payload.customerName || 'Customer',
      customerPhone: payload.customerPhone || '',
      customerCity: payload.customerCity || 'Lebanon',
      productId: payload.product.id,
      productTitle: payload.product.title,
      productCategory: payload.product.category,
      productPrice: payload.product.price,
      productImage: payload.product.image,
      selectedVariants: payload.selectedVariants,
      customNote: payload.customNote,
      status: 'inquiry_sent',
      history: [
        {
          timestamp: dateStr,
          status: 'inquiry_sent',
          label: 'Inquiry Forwarded to WhatsApp',
          note: `Customer forwarded order specs to +961 81773349.`,
        },
      ],
      whatsappMessage: messageText,
    };

    setOrders(prev => [newOrder, ...prev]);

    // Save to user inquiry history in localStorage
    try {
      const userHistory = JSON.parse(localStorage.getItem('coveristic_my_inquiries') || '[]');
      localStorage.setItem('coveristic_my_inquiries', JSON.stringify([newOrder, ...userHistory]));
    } catch (e) {
      console.error(e);
    }

    return { orderId, whatsappUrl };
  };

  const getOrderByIdOrPhone = (query: string): OrderInquiry[] => {
    if (!query.trim()) return [];
    const clean = query.trim().toLowerCase().replace(/[\s\-\+\(\)]/g, '');
    return orders.filter(o => {
      if (o.inRecycleBin) return false;
      const matchId = o.id.toLowerCase().replace(/[\s\-]/g, '').includes(clean);
      const matchPhone = o.customerPhone.replace(/[\s\-\+\(\)]/g, '').includes(clean);
      const matchName = o.customerName.toLowerCase().includes(query.trim().toLowerCase());
      return matchId || matchPhone || matchName;
    });
  };

  const userInquiryHistory = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('coveristic_my_inquiries') || '[]');
    } catch {
      return [];
    }
  }, [orders]);

  // Product mutations
  const addProduct = (productData: Omit<Product, 'id'>): Product => {
    const catCode = (productData.category || 'GE').slice(0, 2).toUpperCase();
    let num = products.length + 1;
    let newId = `COV-${catCode}-${String(num).padStart(2, '0')}`;
    while (products.some(p => p.id === newId)) {
      num++;
      newId = `COV-${catCode}-${String(num).padStart(2, '0')}`;
    }

    const newProduct: Product = enrichProductWithArabic({
      ...productData,
      id: newId,
      image: normalizeImageToWebp(productData.image),
      additionalImages: (productData.additionalImages || []).map(normalizeImageToWebp)
    });

    const updatedCatalog = [newProduct, ...products];
    setProducts(updatedCatalog);
    saveProductsToDb(updatedCatalog).catch(() => {});
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify(updatedCatalog));
    } catch {}
    lastLocalMutationTimestamp.current = Date.now();

    // Automatically regenerate and sync sitemap.xml to disk and server
    syncSitemapToServer(updatedCatalog, categories, pagesSEO).catch(() => {});

    showToast(`Adding "${newProduct.title}" to hosting database...`);

    // Automatic direct save to hosting database (no push button needed!)
    autoSaveProductToHosting(newProduct, products).then(res => {
      if (res.updatedProduct && res.updatedProduct.image !== newProduct.image) {
        setProducts(prev => prev.map(p => p.id === newId ? enrichProductWithArabic(res.updatedProduct) : p));
      }
      showToast(`✓ "${newProduct.title}" saved to hosting database!`);
      refreshHostingHealth();
    }).catch(err => {
      console.warn('Automatic hosting save warning:', err);
    });

    return newProduct;
  };

  const updateProduct = (id: string, updates: Partial<Product>) => {
    let targetUpdated: Product | null = null;
    const newProducts = products.map(p => {
      if (p.id !== id) return p;
      const merged = { ...p, ...updates };
      if (updates.title && !updates.titleAr) {
        delete merged.titleAr;
        delete merged.slugAr;
      }
      if (updates.description && !updates.descriptionAr) {
        delete merged.descriptionAr;
      }
      const updated = enrichProductWithArabic(merged);
      if (updates.image) {
        updated.image = normalizeImageToWebp(updates.image);
      }
      if (updates.additionalImages) {
        updated.additionalImages = updates.additionalImages.map(normalizeImageToWebp);
      }
      targetUpdated = updated;
      return updated;
    });

    setProducts(newProducts);
    saveProductsToDb(newProducts).catch(() => {});
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify(newProducts));
    } catch {}
    lastLocalMutationTimestamp.current = Date.now();

    showToast(`Saving changes to hosting database...`);

    if (targetUpdated) {
      autoSaveProductToHosting(targetUpdated, newProducts).then(() => {
        showToast(`✓ Changes saved to hosting database!`);
        refreshHostingHealth();
      }).catch(() => {});
    } else {
      autoSyncCatalogToHosting(newProducts).then(() => {
        refreshHostingHealth();
      }).catch(() => {});
    }
  };

  const addSavedCustomTag = (tag: { text: string; color: string }) => {
    const cleanText = tag.text.trim().toUpperCase();
    if (!cleanText) return;
    setSavedCustomTags(prev => {
      const exists = prev.some(t => t.text.trim().toUpperCase() === cleanText);
      if (exists) {
        return prev.map(t => t.text.trim().toUpperCase() === cleanText ? { ...t, color: tag.color } : t);
      }
      const newTagItem: SavedCustomTag = {
        id: 'tag-' + Date.now() + '-' + Math.random().toString(36).substring(2, 6),
        text: cleanText,
        color: tag.color || '#E21B24',
        createdAt: new Date().toISOString()
      };
      return [...prev, newTagItem];
    });
    showToast(`Tag "${cleanText}" saved to library`);
  };

  const deleteSavedCustomTag = (tagText: string) => {
    const trimmed = tagText.trim().toLowerCase();
    const cleanText = tagText.trim().toUpperCase();
    setSavedCustomTags(prev => prev.filter(t => t.text.trim().toLowerCase() !== trimmed));
    // Also remove from any products that currently have this tag (in customTag or tags array)
    setProducts(prev => prev.map(p => {
      let changed = false;
      let newCustomTag = p.customTag;
      if (p.customTag && p.customTag.text.trim().toLowerCase() === trimmed) {
        changed = true;
        newCustomTag = undefined;
      }
      let newTags = p.tags || [];
      if (newTags.some(t => t.trim().toLowerCase() === trimmed)) {
        changed = true;
        newTags = newTags.filter(t => t.trim().toLowerCase() !== trimmed);
      }
      if (changed) {
        return { ...p, customTag: newCustomTag, tags: newTags };
      }
      return p;
    }));
    showToast(`Deleted tag "${cleanText}" from saved library`);
  };

  const updateSavedCustomTag = (oldText: string, newTag: { text: string; color: string }) => {
    const oldTrimmed = oldText.trim().toLowerCase();
    const newClean = newTag.text.trim();
    setSavedCustomTags(prev => prev.map(t => {
      if (t.text.trim().toLowerCase() === oldTrimmed) {
        return {
          ...t,
          text: newClean.toUpperCase(),
          color: newTag.color
        };
      }
      return t;
    }));
    updateGlobalCustomTag(oldText, newTag);
  };

  const setProductCustomTag = (productId: string, tag?: { text: string; color: string }) => {
    setProducts(prev =>
      prev.map(p => (p.id === productId ? { ...p, customTag: tag } : p))
    );
    showToast(tag ? `Tag "${tag.text}" saved for product` : `Tag removed from product`);
  };

  const setProductsCustomTagBulk = (productIds: string[], tag?: { text: string; color: string }) => {
    if (productIds.length === 0) return;
    setProducts(prev =>
      prev.map(p => (productIds.includes(p.id) ? { ...p, customTag: tag } : p))
    );
    showToast(
      tag
        ? `Applied tag "${tag.text}" to ${productIds.length} product(s)`
        : `Removed custom tags from ${productIds.length} product(s)`
    );
  };

  const updateGlobalCustomTag = (oldTagText: string, newTag: { text: string; color: string }) => {
    const trimmedOld = oldTagText.trim().toLowerCase();
    const cleanNewText = newTag.text.trim();
    const cleanNewColor = newTag.color.trim();
    let updatedCount = 0;
    setProducts(prev =>
      prev.map(p => {
        let changed = false;
        let newCustomTag = p.customTag;
        if (p.customTag && p.customTag.text.trim().toLowerCase() === trimmedOld) {
          changed = true;
          newCustomTag = {
            text: cleanNewText.toUpperCase(),
            color: cleanNewColor
          };
        }
        let newTags = p.tags || [];
        if (newTags.some(t => t.trim().toLowerCase() === trimmedOld)) {
          changed = true;
          newTags = newTags.map(t => t.trim().toLowerCase() === trimmedOld ? cleanNewText : t);
        }
        if (changed) {
          updatedCount++;
          return {
            ...p,
            customTag: newCustomTag,
            tags: newTags
          };
        }
        return p;
      })
    );
    // Also sync savedCustomTags library
    setSavedCustomTags(prev =>
      prev.map(st => st.text.trim().toLowerCase() === trimmedOld ? { ...st, text: cleanNewText.toUpperCase(), color: cleanNewColor } : st)
    );
    showToast(`Updated tag "${cleanNewText}" across ${updatedCount} product(s)`);
  };

  const removeGlobalCustomTag = (tagText: string) => {
    const trimmed = tagText.trim().toLowerCase();
    let count = 0;
    setProducts(prev =>
      prev.map(p => {
        let changed = false;
        let newCustomTag = p.customTag;
        if (p.customTag && p.customTag.text.trim().toLowerCase() === trimmed) {
          changed = true;
          newCustomTag = undefined;
        }
        let newTags = p.tags || [];
        if (newTags.some(t => t.trim().toLowerCase() === trimmed)) {
          changed = true;
          newTags = newTags.filter(t => t.trim().toLowerCase() !== trimmed);
        }
        if (changed) {
          count++;
          return {
            ...p,
            customTag: newCustomTag,
            tags: newTags
          };
        }
        return p;
      })
    );
    setSavedCustomTags(prev => prev.filter(st => st.text.trim().toLowerCase() !== trimmed));
    showToast(`Removed tag "${tagText}" from ${count} product(s)`);
  };

  const removeAllCustomTags = () => {
    let count = 0;
    setProducts(prev =>
      prev.map(p => {
        if (p.customTag || (p.tags && p.tags.length > 0)) {
          count++;
          const copy = { ...p };
          delete copy.customTag;
          copy.tags = [];
          return copy;
        }
        return p;
      })
    );
    setSavedCustomTags([]);
    try {
      localStorage.setItem('coveristic_saved_tags_v1', JSON.stringify([]));
    } catch {}
    showToast(`Removed all tags`);
  };

  const toggleProductOffersPage = (productId: string, show?: boolean) => {
    setProducts(prev =>
      prev.map(p => {
        if (p.id !== productId) return p;
        const newStatus = typeof show === 'boolean' ? show : !p.showOnOffersPage;
        return {
          ...p,
          showOnOffersPage: newStatus
        };
      })
    );
    showToast(`Product ${show !== false ? 'assigned to' : 'removed from'} Offers page`);
  };

  const setProductsOffersBulk = (productIds: string[], show: boolean) => {
    if (!productIds.length) return;
    setProducts(prev =>
      prev.map(p => {
        if (!productIds.includes(p.id)) return p;
        return {
          ...p,
          showOnOffersPage: show
        };
      })
    );
    showToast(`${show ? 'Assigned' : 'Removed'} ${productIds.length} products ${show ? 'to' : 'from'} Offers page`);
  };

  const assignNewOfferTagToProduct = (productId: string) => {
    const targetTag = { text: 'NEW OFFER', color: '#E21B24' };
    addSavedCustomTag(targetTag);
    setProducts(prev =>
      prev.map(p => {
        if (p.id !== productId) return p;
        return {
          ...p,
          customTag: targetTag,
          showOnOffersPage: true
        };
      })
    );
    showToast(`Tagged with "NEW OFFER" & assigned to Offers page`);
  };

  const moveToRecycleBin = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    const nowIso = new Date().toISOString();
    setProducts(prev =>
      prev.map(p =>
        targetIds.includes(p.id)
          ? { ...p, inRecycleBin: true, trashedAt: nowIso }
          : p
      )
    );
    showToast(
      targetIds.length === 1
        ? 'Product moved to Recycle Bin (Kept for 30 days)'
        : `${targetIds.length} products moved to Recycle Bin (Kept for 30 days)`
    );
  };

  const restoreFromRecycleBin = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    setProducts(prev =>
      prev.map(p =>
        targetIds.includes(p.id)
          ? { ...p, inRecycleBin: false, trashedAt: undefined }
          : p
      )
    );
    showToast(
      targetIds.length === 1
        ? 'Product restored to active catalog'
        : `${targetIds.length} products restored to active catalog`
    );
  };

  const deletePermanently = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    const remaining = products.filter(p => !targetIds.includes(p.id));
    setProducts(remaining);
    saveProductsToDb(remaining).catch(() => {});
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify(remaining));
    } catch {}

    // Send delete directly to hosting database
    for (const targetId of targetIds) {
      fetch('/api/products.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'delete_product', id: targetId })
      }).catch(() => {});
    }
    refreshHostingHealth();
    showToast(
      targetIds.length === 1
        ? 'Product permanently deleted from database'
        : `${targetIds.length} products permanently deleted from database`
    );
  };

  const emptyRecycleBin = () => {
    const trashed = products.filter(p => p.inRecycleBin);
    const activeOnly = products.filter(p => !p.inRecycleBin);
    setProducts(activeOnly);
    saveProductsToDb(activeOnly).catch(() => {});
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify(activeOnly));
    } catch {}

    for (const t of trashed) {
      fetch('/api/products.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'delete_product', id: t.id })
      }).catch(() => {});
    }
    refreshHostingHealth();
    showToast('Recycle Bin emptied permanently from database');
  };

  const setProductsListedStatus = (ids: string | string[], unlisted: boolean) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    setProducts(prev =>
      prev.map(p =>
        targetIds.includes(p.id)
          ? { ...p, unlisted }
          : p
      )
    );
    showToast(
      targetIds.length === 1
        ? `Product ${unlisted ? 'unlisted (hidden from customer store)' : 'listed (visible to customers)'}`
        : `${targetIds.length} products ${unlisted ? 'unlisted in bulk' : 'published in bulk'}`
    );
  };

  const deleteProduct = (id: string) => {
    moveToRecycleBin(id);
  };

  const clearAllProducts = () => {
    hasLoadedInitialRef.current = true;
    setProducts([]);
    setSavedCustomTags([]);
    saveProductsToDb([]).catch(() => {});
    saveSavedTagsToDb([]).catch(() => {});
    try {
      localStorage.setItem('coveristic_products_v3', JSON.stringify([]));
      localStorage.setItem('coveristic_saved_tags_v1', JSON.stringify([]));
    } catch {}
    fetch('/api/products.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode: 'force_clear_all_admin', force_clear: true, products: [] })
    }).catch(() => {});
    showToast('All products, images, and tags removed.');
  };

  const resetDefaultCatalog = () => {
    setProducts(INITIAL_PRODUCTS);
    setCategories(INITIAL_CATEGORIES);
    setPagesContent(INITIAL_PAGES_CONTENT);
    setPagesSEO(INITIAL_PAGES_SEO);
    localStorage.removeItem('coveristic_products_v1');
    localStorage.removeItem('coveristic_categories_v1');
    localStorage.removeItem('coveristic_pages_content_v1');
    localStorage.removeItem('coveristic_pages_seo_v1');
    showToast('Catalog restored to default settings');
  };

  // 1-Click Export of all products and store configuration as JSON
  const exportStoreBackup = () => {
    const backupData: StoreBackupData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      exportedAt: new Date().toLocaleString(),
      storeName: 'Coveristic Store',
      productCount: products.length,
      products: products,
      categories: categories,
      pagesContent: pagesContent,
      pagesSEO: pagesSEO,
    };

    const jsonStr = JSON.stringify(backupData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `coveristic-products-backup-${dateStamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast(`Backup exported (${products.length} products saved)`);
  };

  // 1-Click Complete Backup of all products with images organized into category folders
  const exportFullStoreZipBackup = async (onProgress?: BackupProgressCallback): Promise<void> => {
    try {
      await createAndDownloadStoreZipBackup({
        products,
        categories,
        savedCustomTags,
        pagesContent,
        pagesSEO,
        onProgress
      });
      showToast(`Complete ZIP backup with images downloaded (${products.length} products)`);
    } catch (err: any) {
      console.error('Failed to export ZIP backup:', err);
      showToast(`ZIP backup failed: ${err?.message || 'Unknown error'}`);
      throw err;
    }
  };

  // Restore from a File (either .zip with images or .json)
  const restoreStoreBackupFile = async (
    file: File,
    mode: 'merge' | 'replace' = 'merge',
    onProgress?: BackupProgressCallback,
    options?: { untrashAll?: boolean }
  ): Promise<{ success: boolean; message: string; count: number; imagesRestored: number }> => {
    isRestoringRef.current = true;
    lastLocalMutationTimestamp.current = Date.now();
    try {
      const result = await parseAndRestoreStoreBackup({
        file,
        currentProducts: products,
        mode,
        onProgress,
        untrashAll: options?.untrashAll ?? true
      });

      if (!result.success) {
        showToast(result.message);
        return { success: false, message: result.message, count: 0, imagesRestored: 0 };
      }

      setProducts(result.mergedProducts);
      // Persist to IndexedDB (handles virtually unlimited size and base64 images without QuotaExceededError)
      await saveProductsToDb(result.mergedProducts);
      try {
        localStorage.setItem('coveristic_products_v3', JSON.stringify(result.mergedProducts));
      } catch (e) {
        console.warn('localStorage quota exceeded during restore, data safely stored in IndexedDB', e);
      }

      // 2. Synchronize to hosting database (MySQL & JSON) and upload images
      onProgress?.({
        stage: 'merging',
        message: 'Saving restored products & images to hosting database...',
        percent: 92,
        current: result.mergedProducts.length,
        total: result.mergedProducts.length
      });

      let hostingNotice = '';
      try {
        const syncRes = await syncCatalogToHosting(result.mergedProducts, (p) => {
          onProgress?.({
            stage: p.stage as any,
            message: `Hosting Database: ${p.message}`,
            percent: 90 + Math.round((p.percent / 100) * 10),
            current: p.current,
            total: p.total
          });
        });

        if (syncRes.success && syncRes.sanitizedProducts.length > 0) {
          setProducts(syncRes.sanitizedProducts);
          saveProductsToDb(syncRes.sanitizedProducts).catch(() => {});
          try {
            localStorage.setItem('coveristic_products_v3', JSON.stringify(syncRes.sanitizedProducts));
          } catch {}
          refreshHostingHealth();
          hostingNotice = ` and saved to hosting database (${syncRes.serverCount} products)!`;
        } else {
          hostingNotice = `. (Notice: ${syncRes.message}).`;
        }
      } catch (syncErr: any) {
        console.error('Hosting auto-sync error:', syncErr);
        hostingNotice = `. (Hosting database sync warning: ${syncErr?.message || 'Server unreachable'}).`;
      }

      if (result.categories && Array.isArray(result.categories) && result.categories.length > 0) {
        setCategories(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newCats = (result.categories || []).filter(c => !existingIds.has(c.id));
          const updated = [...prev, ...newCats];
          localStorage.setItem('coveristic_categories_v1', JSON.stringify(updated));
          return updated;
        });
      }

      if (result.savedCustomTags && Array.isArray(result.savedCustomTags) && result.savedCustomTags.length > 0) {
        setSavedCustomTags(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTags = (result.savedCustomTags || []).filter((t: any) => !existingIds.has(t.id));
          const updated = [...prev, ...newTags];
          localStorage.setItem('coveristic_saved_tags_v1', JSON.stringify(updated));
          return updated;
        });
      }

      if (result.pagesContent && typeof result.pagesContent === 'object') {
        setPagesContent(prev => {
          const updated = { ...prev, ...result.pagesContent };
          localStorage.setItem('coveristic_pages_content_v1', JSON.stringify(updated));
          return updated;
        });
      }

      if (result.storiesSettings && typeof result.storiesSettings === 'object') {
        setPagesContent(prev => ({
          ...prev,
          storiesSettings: result.storiesSettings
        }));
        await saveStoriesToDb(result.storiesSettings);
        saveStoriesToHosting(result.storiesSettings).catch(() => {});
      }

      if (result.pagesSEO && Array.isArray(result.pagesSEO) && result.pagesSEO.length > 0) {
        setPagesSEO(result.pagesSEO);
        localStorage.setItem('coveristic_seo_pages_v1', JSON.stringify(result.pagesSEO));
      }

      const finalMsg = `${result.message}${hostingNotice}`;
      showToast(finalMsg);
      return {
        success: true,
        message: finalMsg,
        count: result.mergedProducts.length,
        imagesRestored: result.imagesRestored
      };
    } catch (err: any) {
      const msg = `Restore failed: ${err?.message || 'Unknown error'}`;
      showToast(msg);
      return { success: false, message: msg, count: 0, imagesRestored: 0 };
    } finally {
      setTimeout(() => {
        isRestoringRef.current = false;
        lastLocalMutationTimestamp.current = Date.now();
      }, 5000);
    }
  };

  // Restore or Merge products from a backup JSON string with rock-solid Smart Merge
  const restoreStoreBackup = (jsonData: string, mode: 'merge' | 'replace' = 'merge', options?: { untrashAll?: boolean }): { success: boolean; message: string; count: number } => {
    isRestoringRef.current = true;
    lastLocalMutationTimestamp.current = Date.now();
    try {
      const parsed = JSON.parse(jsonData);
      let incomingProducts: Product[] = [];

      // Accept both full StoreBackupData format and raw Product array
      if (Array.isArray(parsed)) {
        incomingProducts = parsed;
      } else if (parsed && Array.isArray(parsed.products)) {
        incomingProducts = parsed.products;
        // Restore categories if present
        if (Array.isArray(parsed.categories) && parsed.categories.length > 0) {
          setCategories(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newCats = parsed.categories.filter((c: Category) => !existingIds.has(c.id));
            const updated = [...prev, ...newCats];
            localStorage.setItem('coveristic_categories_v1', JSON.stringify(updated));
            return updated;
          });
        }
        if (Array.isArray(parsed.savedCustomTags) && parsed.savedCustomTags.length > 0) {
          setSavedCustomTags(prev => {
            const existingIds = new Set(prev.map(t => t.id));
            const newTags = parsed.savedCustomTags.filter((t: any) => !existingIds.has(t.id));
            const updated = [...prev, ...newTags];
            localStorage.setItem('coveristic_saved_tags_v1', JSON.stringify(updated));
            return updated;
          });
        }
        if (parsed.pagesContent && typeof parsed.pagesContent === 'object') {
          setPagesContent(prev => {
            const updated = { ...prev, ...parsed.pagesContent };
            localStorage.setItem('coveristic_pages_content_v1', JSON.stringify(updated));
            return updated;
          });
        }
        if (parsed.storiesSettings && typeof parsed.storiesSettings === 'object') {
          setPagesContent(prev => ({
            ...prev,
            storiesSettings: parsed.storiesSettings
          }));
          saveStoriesToDb(parsed.storiesSettings).catch(() => {});
          saveStoriesToHosting(parsed.storiesSettings).catch(() => {});
        }
        if (Array.isArray(parsed.pagesSEO) && parsed.pagesSEO.length > 0) {
          setPagesSEO(parsed.pagesSEO);
          localStorage.setItem('coveristic_seo_pages_v1', JSON.stringify(parsed.pagesSEO));
        }
      } else {
        return { success: false, message: 'Invalid backup file format: no products array detected', count: 0 };
      }

      if (incomingProducts.length === 0) {
        return { success: false, message: 'Backup file contains 0 products', count: 0 };
      }

      const untrash = options?.untrashAll ?? true;

      // Sanitize product items
      const validProducts: Product[] = incomingProducts.map((p, idx) => {
        const cat = normalizeCategoryFolder(p.category);
        return {
          id: p.id || `restored-${Date.now()}-${idx}`,
          title: p.title || 'Untitled Product',
          slug: p.slug || `product-${Date.now()}-${idx}`,
          category: cat as any,
          price: typeof p.price === 'number' && p.price > 0 ? p.price : 18,
          comparePrice: p.comparePrice,
          image: p.image || `/images/${cat}/product_${idx + 1}.webp`,
          additionalImages: Array.isArray(p.additionalImages) ? p.additionalImages : [],
          badge: p.badge,
          customTag: p.customTag,
          customTags: Array.isArray(p.customTags) ? p.customTags : (p.customTag ? [p.customTag] : []),
          description: p.description || '',
          details: Array.isArray(p.details) && p.details.length > 0 ? p.details : ['Premium protective build', 'Durable fade-proof print'],
          urbanStyle: p.urbanStyle || 'Urban Streetwear',
          featured: Boolean(p.featured),
          inStock: p.inStock !== false,
          tags: Array.isArray(p.tags) ? p.tags : [],
          variants: Array.isArray(p.variants) ? p.variants : [],
          unlisted: Boolean(p.unlisted),
          inRecycleBin: untrash ? false : Boolean(p.inRecycleBin),
          trashedAt: untrash ? undefined : p.trashedAt,
          showOnOffersPage: Boolean(p.showOnOffersPage),
          offerDiscountText: p.offerDiscountText,
        };
      });

      let finalProducts: Product[] = [];
      let summaryMsg = '';

      if (mode === 'replace') {
        finalProducts = validProducts;
        setProducts(validProducts);
        summaryMsg = `Catalog replaced with ${validProducts.length} restored products`;
      } else {
        // SMART MERGE (RECOMMENDED): resolves by ID, Slug, or (Title + Category); deep merges custom tags, variants, images without duplicates
        const mergeResult = executeSmartMerge(products, validProducts);
        finalProducts = mergeResult.mergedProducts;
        setProducts(mergeResult.mergedProducts);
        summaryMsg = `Smart Merge completed: ${mergeResult.addedCount} newly added, ${mergeResult.updatedCount} updated.`;
      }

      // Persist to IndexedDB and localStorage
      saveProductsToDb(finalProducts).catch(() => {});
      try {
        localStorage.setItem('coveristic_products_v3', JSON.stringify(finalProducts));
      } catch (e) {
        console.warn('localStorage quota exceeded, data safely stored in IndexedDB', e);
      }

      // Automatically synchronize all restored products and images to hosting database
      syncCatalogToHosting(finalProducts).then(syncRes => {
        if (syncRes.success && syncRes.sanitizedProducts.length > 0) {
          setProducts(syncRes.sanitizedProducts);
          saveProductsToDb(syncRes.sanitizedProducts).catch(() => {});
          try {
            localStorage.setItem('coveristic_products_v3', JSON.stringify(syncRes.sanitizedProducts));
          } catch {}
          refreshHostingHealth();
          showToast(`✅ Saved ${syncRes.serverCount} products to hosting database!`);
        } else {
          showToast(`Restored locally. Click "Hosting Database" to push to hosting server.`);
        }
      }).catch(err => {
        console.error('Hosting auto-sync error:', err);
        showToast(`Restored locally. Please sync to hosting database in the Admin Portal.`);
      });

      showToast(summaryMsg);
      return { 
        success: true, 
        message: summaryMsg, 
        count: finalProducts.length 
      };
    } catch (err: any) {
      return { success: false, message: `Failed to parse backup JSON: ${err.message || 'Unknown error'}`, count: 0 };
    } finally {
      setTimeout(() => {
        isRestoringRef.current = false;
        lastLocalMutationTimestamp.current = Date.now();
      }, 5000);
    }
  };

  // Stories 1-Click Export of all stories and lookbooks as JSON
  const exportStoriesBackup = () => {
    const stories = pagesContent.storiesSettings || DEFAULT_STORIES;
    exportStoriesJsonBackup(stories);
    showToast(`Stories backup exported (${stories.items?.length || 0} stories saved)`);
  };

  // Stories 1-Click Complete Backup with images bundled in images/stories/ (.zip)
  const exportStoriesZipBackup = async (onProgress?: BackupProgressCallback): Promise<void> => {
    const stories = pagesContent.storiesSettings || DEFAULT_STORIES;
    try {
      await createAndDownloadStoriesZipBackup({
        storiesSettings: stories,
        onProgress
      });
      showToast(`Stories ZIP backup with images downloaded (${stories.items?.length || 0} stories)`);
    } catch (err: any) {
      console.error('Failed to export stories ZIP backup:', err);
      showToast(`Stories ZIP backup failed: ${err?.message || 'Unknown error'}`);
      throw err;
    }
  };

  // Stories Restore from file (.zip with images or .json)
  const restoreStoriesBackupFile = async (
    file: File,
    mode: 'merge' | 'replace' = 'merge',
    onProgress?: BackupProgressCallback
  ): Promise<{ success: boolean; message: string; count: number; imagesRestored: number }> => {
    try {
      const currentStories = pagesContent.storiesSettings || DEFAULT_STORIES;
      const result = await parseAndRestoreStoriesBackup({
        file,
        currentStories,
        mode,
        onProgress
      });

      if (!result.success) {
        showToast(result.message);
        return { success: false, message: result.message, count: 0, imagesRestored: 0 };
      }

      setPagesContent(prev => ({
        ...prev,
        storiesSettings: result.storiesSettings
      }));

      // Persist to IndexedDB database
      await saveStoriesToDb(result.storiesSettings);
      try {
        localStorage.setItem('coveristic_pages_content_v6', JSON.stringify({
          ...pagesContent,
          storiesSettings: result.storiesSettings
        }));
      } catch {}

      // Persist to Hosting server/database
      await saveStoriesToHosting(result.storiesSettings).catch(() => {});

      showToast(result.message);
      return {
        success: true,
        message: result.message,
        count: result.storiesSettings.items?.length || 0,
        imagesRestored: result.imagesRestored
      };
    } catch (err: any) {
      const msg = `Failed to restore stories: ${err?.message || 'Unknown error'}`;
      showToast(msg);
      return { success: false, message: msg, count: 0, imagesRestored: 0 };
    }
  };

  // Category mutations
  const addCategory = (cat: Category) => {
    const updatedCategories = [...categories, cat];
    setCategories(updatedCategories);
    showToast(`Category "${cat.name}" created`);
    // Automatically regenerate and sync sitemap.xml to disk and server
    syncSitemapToServer(products, updatedCategories, pagesSEO).catch(() => {});
  };

  const updateCategory = (id: string, updates: Partial<Category>) => {
    setCategories(prev =>
      prev.map(c => (c.id === id ? { ...c, ...updates } : c))
    );
    showToast(`Category updated`);
  };

  const deleteCategory = (id: string) => {
    setCategories(prev => prev.filter(c => c.id !== id));
    showToast(`Category deleted`);
  };

  const updateCategoriesBulk = (ids: string[], updates: Partial<Category>) => {
    if (ids.length === 0) return;
    setCategories(prev =>
      prev.map(c => (ids.includes(c.id) ? { ...c, ...updates } : c))
    );
    showToast(`Updated ${ids.length} category/categories`);
  };

  const deleteCategoriesBulk = (ids: string[]) => {
    if (ids.length === 0) return;
    setCategories(prev => prev.filter(c => !ids.includes(c.id)));
    showToast(`Deleted ${ids.length} category/categories`);
  };

  // Page Content mutations
  const updatePagesContent = (content: Partial<SitePagesContent>) => {
    setPagesContent(prev => ({
      ...prev,
      ...content,
    }));
    showToast('Page content updated successfully');
  };

  // News Ticker mutations
  const newsTicker = pagesContent.newsTicker || DEFAULT_NEWS_TICKER;

  const updateNewsTicker = (settings: Partial<NewsTickerSettings>) => {
    setPagesContent(prev => {
      const current = prev.newsTicker || DEFAULT_NEWS_TICKER;
      const updated = { ...current, ...settings };
      return {
        ...prev,
        newsTicker: updated,
      };
    });
    showToast('News ticker updated successfully');
  };

  const addNewsTickerItem = (item: Omit<NewsTickerItem, 'id'>) => {
    const newItem: NewsTickerItem = {
      ...item,
      id: `news-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    };
    setPagesContent(prev => {
      const current = prev.newsTicker || DEFAULT_NEWS_TICKER;
      return {
        ...prev,
        newsTicker: {
          ...current,
          items: [...current.items, newItem],
        },
      };
    });
    showToast('New announcement added to ticker');
  };

  const updateNewsTickerItem = (id: string, updates: Partial<NewsTickerItem>) => {
    setPagesContent(prev => {
      const current = prev.newsTicker || DEFAULT_NEWS_TICKER;
      return {
        ...prev,
        newsTicker: {
          ...current,
          items: current.items.map(item => (item.id === id ? { ...item, ...updates } : item)),
        },
      };
    });
    showToast('Announcement updated');
  };

  const deleteNewsTickerItem = (id: string) => {
    setPagesContent(prev => {
      const current = prev.newsTicker || DEFAULT_NEWS_TICKER;
      return {
        ...prev,
        newsTicker: {
          ...current,
          items: current.items.filter(item => item.id !== id),
        },
      };
    });
    showToast('Announcement removed from ticker');
  };

  const resetNewsTickerDefaults = () => {
    setPagesContent(prev => ({
      ...prev,
      newsTicker: DEFAULT_NEWS_TICKER,
    }));
    showToast('News ticker reset to default settings');
  };

  const tagDisplaySize: TagDisplaySize = pagesContent.tagDisplaySettings?.size || 'md';

  const setTagDisplaySize = (size: TagDisplaySize) => {
    updatePagesContent({
      tagDisplaySettings: {
        ...(pagesContent.tagDisplaySettings || { showDot: true }),
        size,
      },
    });
    showToast(`Product tag size adjusted to ${size.toUpperCase()}`);
  };

  // Helper to persist SEO array to /api/seo (and /api/seo.php) -> seo.json
  const persistPagesSEOToApi = async (list: PageSEO[]): Promise<boolean> => {
    try {
      localStorage.setItem('coveristic_pages_seo_v2', JSON.stringify(list));
    } catch {}

    const endpoints = ['/api/seo', '/api/seo.php'];
    for (const endpoint of endpoints) {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pagesSEO: list })
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.success) {
            return true;
          }
        }
      } catch {
        // try next endpoint
      }
    }
    return false;
  };

  // SEO mutations
  const updatePageSEO = (pageId: string, seo: Partial<PageSEO>) => {
    const nowIso = new Date().toISOString();
    setPagesSEO(prev => {
      const updated = prev.map(p =>
        p.id === pageId
          ? { ...p, ...seo, lastUpdated: nowIso }
          : p
      );
      persistPagesSEOToApi(updated).catch(() => {});
      return updated;
    });
    showToast(`SEO metadata for "${seo.name || pageId}" saved to seo.json`);
  };

  const saveAllPagesSEO = async (pages: PageSEO[]): Promise<boolean> => {
    const nowIso = new Date().toISOString();
    const stamped = pages.map(p => ({
      ...p,
      lastUpdated: p.lastUpdated || nowIso
    }));
    setPagesSEO(stamped);
    const ok = await persistPagesSEOToApi(stamped);
    showToast(ok ? `Saved all ${stamped.length} pages to seo.json` : `Saved ${stamped.length} pages locally`);
    return ok;
  };

  const addPageSEO = async (page: PageSEO): Promise<boolean> => {
    const nowIso = new Date().toISOString();
    const cleanPath = page.path.startsWith('/') ? page.path : `/${page.path}`;
    const newEntry: PageSEO = {
      ...page,
      id: page.id || cleanPath.replace(/^\/+/, '').replace(/[^a-z0-9-_]/gi, '-') || `page-${Date.now()}`,
      path: cleanPath,
      canonicalUrl: page.canonicalUrl || `https://coveristic.net${cleanPath === '/' ? '' : cleanPath}`,
      ogImage: page.ogImage || '/images/catalog/img_18.webp',
      ogType: page.ogType || 'website',
      lastUpdated: nowIso
    };
    let nextList: PageSEO[] = [];
    setPagesSEO(prev => {
      const existsIdx = prev.findIndex(p => p.id === newEntry.id || p.path === newEntry.path);
      if (existsIdx >= 0) {
        nextList = prev.map((p, idx) => (idx === existsIdx ? newEntry : p));
      } else {
        nextList = [...prev, newEntry];
      }
      return nextList;
    });
    const ok = await persistPagesSEOToApi(nextList);
    showToast(`Added "${newEntry.name}" (${newEntry.path}) to seo.json`);
    return ok;
  };

  const deletePageSEO = async (pageId: string): Promise<boolean> => {
    let nextList: PageSEO[] = [];
    setPagesSEO(prev => {
      nextList = prev.filter(p => p.id !== pageId);
      return nextList;
    });
    const ok = await persistPagesSEOToApi(nextList);
    showToast(`Removed page "${pageId}" from seo.json`);
    return ok;
  };

  const resetPagesSEO = async (): Promise<boolean> => {
    setPagesSEO(INITIAL_PAGES_SEO);
    const ok = await persistPagesSEOToApi(INITIAL_PAGES_SEO);
    showToast('Reset all page SEO metadata to defaults and saved to seo.json');
    return ok;
  };

  // Order status mutations
  const updateOrderStatus = (orderId: string, newStatus: OrderStatus, note?: string) => {
    const statusLabels: Record<OrderStatus, string> = {
      inquiry_sent: 'Inquiry Received on WhatsApp',
      whatsapp_confirmed: 'Confirmed & Approved via WhatsApp',
      in_production: 'In Print Production',
      shipped_local: 'Dispatched with Express Courier',
      delivered: 'Delivered to Customer',
      cancelled: 'Inquiry Cancelled',
    };

    setOrders(prev =>
      prev.map(o => {
        if (o.id === orderId) {
          const newHistory = [
            ...o.history,
            {
              timestamp: new Date().toISOString(),
              status: newStatus,
              label: statusLabels[newStatus],
              note: note || `Status updated to ${statusLabels[newStatus]}`,
            },
          ];
          return {
            ...o,
            status: newStatus,
            history: newHistory,
          };
        }
        return o;
      })
    );
    showToast(`Order #${orderId} updated to ${statusLabels[newStatus]}`);
  };

  const moveOrdersToRecycleBin = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    const now = new Date().toISOString();
    setOrders(prev =>
      prev.map(o =>
        targetIds.includes(o.id)
          ? { ...o, inRecycleBin: true, trashedAt: now }
          : o
      )
    );
    showToast(
      targetIds.length === 1
        ? 'Order moved to Recycle Bin (Kept for 30 days)'
        : `${targetIds.length} orders moved to Recycle Bin (Kept for 30 days)`
    );
  };

  const restoreOrdersFromRecycleBin = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    setOrders(prev =>
      prev.map(o =>
        targetIds.includes(o.id)
          ? { ...o, inRecycleBin: false, trashedAt: undefined }
          : o
      )
    );
    showToast(
      targetIds.length === 1
        ? 'Order restored from Recycle Bin'
        : `${targetIds.length} orders restored from Recycle Bin`
    );
  };

  const deleteOrdersPermanently = (ids: string | string[]) => {
    const targetIds = Array.isArray(ids) ? ids : [ids];
    if (targetIds.length === 0) return;
    setOrders(prev => prev.filter(o => !targetIds.includes(o.id)));
    showToast(
      targetIds.length === 1
        ? 'Order permanently deleted'
        : `${targetIds.length} orders permanently deleted`
    );
  };

  const emptyOrdersRecycleBin = () => {
    setOrders(prev => prev.filter(o => !o.inRecycleBin));
    showToast('Orders Recycle Bin emptied permanently');
  };

  const addBlogArticle = (article: Omit<BlogArticle, 'id' | 'publishedAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    const newArt: BlogArticle = {
      ...article,
      id: `art-${Date.now().toString(36)}`,
      publishedAt: now,
      updatedAt: now
    };
    setBlogArticles(prev => [newArt, ...prev]);
    // Automatically persist to hosting MySQL database
    saveHostingBlogArticle(newArt).catch(err => {
      console.warn('Failed to auto-save new blog article to hosting MySQL', err);
    });
  };

  const updateBlogArticle = (id: string, updates: Partial<BlogArticle>) => {
    const now = new Date().toISOString();
    let updatedArt: BlogArticle | null = null;
    setBlogArticles(prev => prev.map(a => {
      if (a.id === id) {
        updatedArt = { ...a, ...updates, updatedAt: now };
        return updatedArt;
      }
      return a;
    }));
    if (updatedArt) {
      // Automatically persist edits to hosting MySQL database
      saveHostingBlogArticle(updatedArt).catch(err => {
        console.warn('Failed to auto-save updated blog article to hosting MySQL', err);
      });
    }
  };

  const deleteBlogArticle = (id: string) => {
    setBlogArticles(prev => prev.filter(a => a.id !== id));
    // Automatically delete from hosting MySQL database
    deleteHostingBlogArticle(id).catch(err => {
      console.warn('Failed to delete blog article from hosting MySQL', err);
    });
  };

  const toggleArchiveBlogArticle = (id: string) => {
    const now = new Date().toISOString();
    let updatedArt: BlogArticle | null = null;
    setBlogArticles(prev => prev.map(a => {
      if (a.id === id) {
        updatedArt = {
          ...a,
          status: a.status === 'published' ? 'archived' : 'published',
          updatedAt: now
        };
        return updatedArt;
      }
      return a;
    }));
    if (updatedArt) {
      saveHostingBlogArticle(updatedArt).catch(err => {
        console.warn('Failed to auto-save archived status to hosting MySQL', err);
      });
    }
  };

  const resetBlogArticles = () => {
    setBlogArticles(INITIAL_BLOG_ARTICLES);
    try {
      localStorage.setItem('coveristic_blog_articles_v2', JSON.stringify(INITIAL_BLOG_ARTICLES));
    } catch {}
    saveAllHostingBlogArticles(INITIAL_BLOG_ARTICLES).catch(err => {
      console.warn('Failed to reset blog articles in hosting MySQL', err);
    });
  };

  // Real Blog Analytics Tracking (Views, Unique Readers, WhatsApp Leads, Read Duration, Reset)
  const recordBlogArticleView = (
    slugOrId: string,
    device: 'mobile' | 'desktop' | 'tablet' = 'mobile',
    referrer: 'google' | 'whatsapp' | 'social' | 'direct' = 'direct'
  ) => {
    if (!slugOrId) return;
    const clean = slugOrId.toLowerCase().trim();
    const todayKey = new Date().toISOString().slice(0, 10);

    // Check if this browser has already viewed this article
    let isUnique = true;
    try {
      const seenRaw = localStorage.getItem('coveristic_seen_blog_articles_v1');
      const seenList: string[] = seenRaw ? JSON.parse(seenRaw) : [];
      if (seenList.includes(clean)) {
        isUnique = false;
      } else {
        seenList.push(clean);
        localStorage.setItem('coveristic_seen_blog_articles_v1', JSON.stringify(seenList));
      }
    } catch {}

    let updatedArt: BlogArticle | null = null;
    setBlogArticles(prev =>
      prev.map(a => {
        if (a.slug.toLowerCase() === clean || a.id.toLowerCase() === clean) {
          const prevViews = Math.max(0, Number(a.views || 0));
          const prevUniques = Math.max(0, Number(a.uniqueVisitors || 0));
          const prevDaily = a.dailyHistory ? { ...a.dailyHistory } : {};
          const todayEntry = prevDaily[todayKey] || { views: 0, uniques: 0, whatsapp: 0 };
          prevDaily[todayKey] = {
            views: todayEntry.views + 1,
            uniques: todayEntry.uniques + (isUnique ? 1 : 0),
            whatsapp: todayEntry.whatsapp
          };

          const prevDevices = a.deviceCounts || { mobile: 0, desktop: 0, tablet: 0 };
          const prevReferrals = a.referralCounts || { google: 0, whatsapp: 0, social: 0, direct: 0 };

          updatedArt = {
            ...a,
            views: prevViews + 1,
            uniqueVisitors: prevUniques + (isUnique ? 1 : 0),
            dailyHistory: prevDaily,
            deviceCounts: {
              ...prevDevices,
              [device]: (prevDevices[device] || 0) + 1
            },
            referralCounts: {
              ...prevReferrals,
              [referrer]: (prevReferrals[referrer] || 0) + 1
            }
          };
          return updatedArt;
        }
        return a;
      })
    );

    if (updatedArt) {
      saveHostingBlogArticle(updatedArt).catch(() => {});
    }
  };

  const recordBlogWhatsappClick = (slugOrId: string) => {
    if (!slugOrId) return;
    const clean = slugOrId.toLowerCase().trim();
    const todayKey = new Date().toISOString().slice(0, 10);

    let updatedArt: BlogArticle | null = null;
    setBlogArticles(prev =>
      prev.map(a => {
        if (a.slug.toLowerCase() === clean || a.id.toLowerCase() === clean) {
          const prevWhatsapp = Math.max(0, Number(a.whatsappClicks || 0));
          const prevDaily = a.dailyHistory ? { ...a.dailyHistory } : {};
          const todayEntry = prevDaily[todayKey] || { views: 0, uniques: 0, whatsapp: 0 };
          prevDaily[todayKey] = {
            ...todayEntry,
            whatsapp: todayEntry.whatsapp + 1
          };

          updatedArt = {
            ...a,
            whatsappClicks: prevWhatsapp + 1,
            dailyHistory: prevDaily
          };
          return updatedArt;
        }
        return a;
      })
    );

    if (updatedArt) {
      saveHostingBlogArticle(updatedArt).catch(() => {});
    }
  };

  const recordBlogReadDuration = (slugOrId: string, seconds: number) => {
    if (!slugOrId || seconds <= 0) return;
    const clean = slugOrId.toLowerCase().trim();
    const clampedSec = Math.min(900, Math.max(1, Math.round(seconds)));

    let updatedArt: BlogArticle | null = null;
    setBlogArticles(prev =>
      prev.map(a => {
        if (a.slug.toLowerCase() === clean || a.id.toLowerCase() === clean) {
          updatedArt = {
            ...a,
            totalReadSeconds: Math.max(0, Number(a.totalReadSeconds || 0)) + clampedSec,
            readSessions: Math.max(0, Number(a.readSessions || 0)) + 1
          };
          return updatedArt;
        }
        return a;
      })
    );

    if (updatedArt) {
      saveHostingBlogArticle(updatedArt).catch(() => {});
    }
  };

  const resetBlogAnalytics = async () => {
    try {
      localStorage.removeItem('coveristic_seen_blog_articles_v1');
    } catch {}

    let resetList: BlogArticle[] = [];
    setBlogArticles(prev => {
      resetList = prev.map(a => ({
        ...a,
        views: 0,
        uniqueVisitors: 0,
        whatsappClicks: 0,
        totalReadSeconds: 0,
        readSessions: 0,
        dailyHistory: {},
        deviceCounts: { mobile: 0, desktop: 0, tablet: 0 },
        referralCounts: { google: 0, whatsapp: 0, social: 0, direct: 0 }
      }));
      try {
        localStorage.setItem('coveristic_blog_articles_v2', JSON.stringify(resetList));
      } catch {}
      return resetList;
    });

    await resetHostingBlogAnalytics();
    if (resetList.length > 0) {
      await saveAllHostingBlogArticles(resetList).catch(() => {});
    }
    showToast('All blog analytics counters have been reset to 0.');
  };

  const applyStoreStateSnapshot = (snapshot: {
    products?: Product[];
    categories?: Category[];
    pagesContent?: SitePagesContent;
    blogArticles?: BlogArticle[];
    pagesSEO?: PageSEO[];
  }) => {
    lastLocalMutationTimestamp.current = Date.now();
    if (snapshot.products) {
      setProducts(snapshot.products);
      autoSyncCatalogToHosting(snapshot.products);
    }
    if (snapshot.categories) {
      setCategories(snapshot.categories);
    }
    if (snapshot.pagesContent) {
      setPagesContent(snapshot.pagesContent);
    }
    if (snapshot.blogArticles) {
      setBlogArticles(snapshot.blogArticles);
      saveAllHostingBlogArticles(snapshot.blogArticles).catch(() => {});
    }
    if (snapshot.pagesSEO) {
      setPagesSEO(snapshot.pagesSEO);
    }
  };

  const regenerateAndSyncSitemap = async (customBaseUrl?: string): Promise<SitemapSyncResult> => {
    const result = await syncSitemapToServer(products, categories, pagesSEO, pagesContent, customBaseUrl, blogArticles);
    showToast('sitemap updated');
    return result;
  };

  const regenerateAndSyncRss = async (customBaseUrl?: string): Promise<RssSyncResult> => {
    const result = await syncRssToServer(products, categories, blogArticles, customBaseUrl);
    showToast('RSS feed URL regenerated and verified');
    return result;
  };

  return (
    <StoreContext.Provider
      value={{
        products,
        isInitialLoading,
        categories,
        pagesContent,
        pagesSEO,
        orders,
        blogArticles,
        selectedBlogSlug,
        setSelectedBlogSlug,
        addBlogArticle,
        updateBlogArticle,
        deleteBlogArticle,
        toggleArchiveBlogArticle,
        resetBlogArticles,
        recordBlogArticleView,
        recordBlogWhatsappClick,
        recordBlogReadDuration,
        resetBlogAnalytics,
        applyStoreStateSnapshot,
        currentView,
        setCurrentView,
        activeCategoryFilter,
        setActiveCategoryFilter,
        activeTagFilter,
        setActiveTagFilter,
        filterByTagAndCategory,
        searchQuery,
        setSearchQuery,
        selectedProduct,
        setSelectedProduct,
        currency,
        setCurrency,
        formatPrice,
        language,
        setLanguage,
        t,
        isRtl,
        createAndSendWhatsAppOrder,
        getOrderByIdOrPhone,
        userInquiryHistory,
        isAdminAuthenticated,
        setIsAdminAuthenticated,
        verifyAdminCredentials,
        completeAdminLogin,
        authenticateAdmin,
        logoutAdmin,
        addProduct,
        updateProduct,
        deleteProduct,
        savedCustomTags,
        addSavedCustomTag,
        deleteSavedCustomTag,
        updateSavedCustomTag,
        setProductCustomTag,
        setProductsCustomTagBulk,
        updateGlobalCustomTag,
        removeGlobalCustomTag,
        removeAllCustomTags,
        moveToRecycleBin,
        restoreFromRecycleBin,
        deletePermanently,
        emptyRecycleBin,
        setProductsListedStatus,
        resetDefaultCatalog,
        clearAllProducts,
        exportStoreBackup,
        exportFullStoreZipBackup,
        restoreStoreBackup,
        restoreStoreBackupFile,
        exportStoriesBackup,
        exportStoriesZipBackup,
        restoreStoriesBackupFile,
        addCategory,
        updateCategory,
        deleteCategory,
        updateCategoriesBulk,
        deleteCategoriesBulk,
        updatePagesContent,
        updatePageSEO,
        saveAllPagesSEO,
        addPageSEO,
        deletePageSEO,
        resetPagesSEO,
        newsTicker,
        updateNewsTicker,
        addNewsTickerItem,
        updateNewsTickerItem,
        deleteNewsTickerItem,
        resetNewsTickerDefaults,
        updateOrderStatus,
        moveOrdersToRecycleBin,
        restoreOrdersFromRecycleBin,
        deleteOrdersPermanently,
        emptyOrdersRecycleBin,
        toastMessage,
        showToast,
        isProductAnOffer,
        toggleProductOffersPage,
        setProductsOffersBulk,
        assignNewOfferTagToProduct,
        tagDisplaySize,
        setTagDisplaySize,
        hostingHealth,
        refreshHostingHealth,
        syncToHostingDb,
        regenerateAndSyncSitemap,
        regenerateAndSyncRss,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};
